// Pre-checkout onboarding funnel. Originally shipped as mobile-only in
// commit f975f49. Now used on BOTH mobile and desktop as a full-screen
// overlay that opens when the user clicks "Get started". Flow: CLI intro
// → hours picker (4 buckets) → monthly goal → niche wheel → price tier
// → personalized earnings projection → Stripe checkout.
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Zap, Check, Search, Plus, Sparkles, Wrench, Wind, Trees, Home as HomeIcon, Moon, Sun, Briefcase, Rocket, Send, Calendar, Phone, ImageIcon, MessageSquare, HelpCircle, Map, DollarSign, Mail } from 'lucide-react';
import { logAnalyticsEvent } from '@/lib/firebase';
import { prefetchHostedCheckout } from '@/lib/hostedCheckoutPrefetch';
import { Button } from '@/components/ui/Button';
import { HackerLoadingScreen } from '@/components/ui/HackerLoadingScreen';
import { isLaunchWeekActive, LAUNCH_REGULAR_PRICES } from '@/components/ui/LaunchCountdown';
import { PLAN_CONFIGS, isOneTimePlan, type PlanId } from '@shared/types';
import { isMobileNow } from '@/hooks/useIsMobile';

// navigator.vibrate no-op on desktop; safe to call unconditionally.
const haptic = (pattern: number | number[] = 12) => {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try { navigator.vibrate(pattern); } catch { /* noop */ }
  }
};


type HoursId = '2-3' | '4-6' | '7-10' | '10+';
type GoalId = '1k' | '3k' | '5k' | '10k';
type NicheId = 'plumbers' | 'electricians' | 'hvac' | 'landscapers' | 'roofers' | 'cleaners';
type PriceTier = 300 | 500 | 800 | 1200;

interface FunnelAnswers {
  hours: HoursId | null;
  goal: GoalId | null;
  niche: NicheId | null;
  price: PriceTier | null;
}

// Discrete bucket options. Each hours bucket carries a conservative target
// for closes/month, calibrated so the displayed ROI stays punchy but
// defensible - impulse buyers coming from social tune out at "too good
// to be true" multipliers, so we cap the headline ROI around 50-60x.
const HOURS_OPTIONS: { id: HoursId; label: string; sub: string; closes: number }[] = [
  { id: '2-3',  label: '2–3 hours / week',  sub: 'Side income on evenings',  closes: 2 },
  { id: '4-6',  label: '4–6 hours / week',  sub: 'Real money on weekends',   closes: 5 },
  { id: '7-10', label: '7–10 hours / week', sub: 'Quit-the-day-job pace',    closes: 10 },
  { id: '10+',  label: '10+ hours / week',  sub: 'Go full send',             closes: 15 },
];

const GOAL_OPTIONS: { id: GoalId; label: string; sub: string; amount: number }[] = [
  { id: '1k',  label: '$1,000 / month',   sub: 'Cover the rent',    amount: 1000  },
  { id: '3k',  label: '$3,000 / month',   sub: 'Pay off the debt',  amount: 3000  },
  { id: '5k',  label: '$5,000 / month',   sub: 'Real freedom',      amount: 5000  },
  { id: '10k', label: '$10,000+ / month', sub: 'Full replacement',  amount: 10000 },
];

const PRICE_OPTIONS: { id: PriceTier; label: string; sub: string }[] = [
  { id: 300,  label: '$300',   sub: 'Easiest yes' },
  { id: 500,  label: '$500',   sub: 'Sweet spot' },
  { id: 800,  label: '$800',   sub: 'Premium positioning' },
  { id: 1200, label: '$1,200', sub: 'Agency tier' },
];

const NICHE_OPTIONS: { id: NicheId; label: string; Icon: typeof Wrench }[] = [
  { id: 'plumbers',     label: 'Plumbers',     Icon: Wrench },
  { id: 'electricians', label: 'Electricians', Icon: Zap },
  { id: 'hvac',         label: 'HVAC techs',   Icon: Wind },
  { id: 'landscapers',  label: 'Landscapers',  Icon: Trees },
  { id: 'roofers',      label: 'Roofers',      Icon: HomeIcon },
  { id: 'cleaners',     label: 'Cleaners',     Icon: Sparkles },
];

// The 3 plans exposed in the funnel's plan picker. Agency is intentionally
// hidden - priced for people who already know they need it, not for
// onboarding conversion.
const FUNNEL_PICKER_PLANS: PlanId[] = ['starter', 'cashflow', 'scale'];
// MOBILE (2026-07-24): a two-card choice, Lifetime against the cheapest
// subscription, shown side by side rather than stacked. Lifetime is the default.
// Deliberately NOT the three-tier ladder: on a phone the stack ran past the fold
// and the take-home figure - the whole point of the screen - fell off it.
const MOBILE_PICKER_PLANS: PlanId[] = ['lifetime', 'starter'];

// Which plan we pre-highlight based on the user's hours answer. Starter's
// 12-sites/month cap bites first, so anyone doing 7+ hrs/wk gets nudged up.
const RECOMMENDED_PLAN: Record<HoursId, PlanId> = {
  '2-3':  'starter',
  '4-6':  'starter',
  '7-10': 'cashflow',
  '10+':  'cashflow',
};

// Identity priming: each hours bucket earns the user a one-liner that
// crowns a self-concept tied to the commitment level they just chose. Pure
// framing (no factual claim), safe to ship.
const IDENTITY: Record<HoursId, { label: string; sub: string }> = {
  '2-3':  { label: "You're a weekend-only founder.",        sub: "With these stats, Starter fits your pace." },
  '4-6':  { label: "You're building a real side business.", sub: "With these stats, Starter comfortably handles your pace." },
  '7-10': { label: "You're going semi-pro.",                sub: "With these stats, you'll outgrow Starter fast." },
  '10+':  { label: "You're going full-send.",               sub: "With these stats, Starter won't keep up." },
};

// Descriptive positioning per plan. Kept generic and product-focused -
// deliberately no user counts, no first-sale day claims, no outcome
// promises. Anything quantitative ships only when we can back it with
// live Firestore metrics.
const PLAN_POSITION: Record<PlanId, string | null> = {
  free:     null,
  starter:  'Lowest-friction entry to land your first sales.',
  cashflow: 'Best ROI for 7+ hrs / week. 4× the capacity of Starter.',
  scale:    'Max throughput for full-time operators.',
  agency:   null,
  // Neither is offered any more. Both stay in PlanId so the users who bought
  // them before 2026-07-24 still resolve to a real config instead of crashing
  // on an undefined lookup.
  monthly:  null,
  lifetime: 'Pay once. No monthly bill, ever.',
  lifetime_offer: 'Pay once. No monthly bill, ever.',
};

// Smooth ease-out count-up for the big earnings number.
function useCountUp(target: number, duration = 1600, trigger: unknown = target) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target <= 0) { setValue(0); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, target, duration]);
  return value;
}

interface OnboardingFunnelProps {
  onClose: (atStep: number) => void;
  onCheckout: (planId: PlanId, projectedMonthly: number) => void;
  loading: boolean;
}

// Persisted funnel state lives here - restored on remount so users who
// close the overlay mid-flow pick up exactly where they left off instead of
// re-answering questions. Scoped to a single localStorage key and bumped
// via the _v suffix when the shape changes.
const FUNNEL_STORAGE_KEY = 'romulus_funnel_v1';
type PersistedFunnelState = { step: number; answers: FunnelAnswers; savedAt: number };
const FUNNEL_STALE_MS = 24 * 60 * 60 * 1000; // 24h

function loadPersistedFunnel(): PersistedFunnelState | null {
  try {
    const raw = localStorage.getItem(FUNNEL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedFunnelState;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > FUNNEL_STALE_MS) return null;
    // Don't resume the sim or reveal steps - those are transient. Bounce
    // the user back to the last question they answered.
    if (parsed.step >= 5) return { ...parsed, step: 4 };
    return parsed;
  } catch { return null; }
}

export function OnboardingFunnel({ onClose, onCheckout, loading }: OnboardingFunnelProps) {
  // isMobileNow(), resolved once and frozen. useIsMobile() starts false and
  // corrects in an effect, which would render a tappable X for one paint on a
  // phone before removing it - i.e. a real escape hatch from a funnel that is
  // supposed to have none. Frozen rather than live so rotating the device
  // mid-funnel cannot hand someone the close button either.
  const [isMobile] = useState(() => isMobileNow());
  const persisted = typeof window !== 'undefined' ? loadPersistedFunnel() : null;
  const [step, setStep] = useState(persisted?.step ?? 0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState<FunnelAnswers>(persisted?.answers ?? {
    hours: null,
    goal: null,
    niche: null,
    price: null,
  });

  // Persist funnel state whenever answers or step change. Skip the sim +
  // reveal steps (5, 6) since those don't capture new decisions.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (step >= 5) return;
    try {
      localStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify({ step, answers, savedAt: Date.now() }));
    } catch { /* quota full or disabled */ }
  }, [step, answers]);
  // How long the user-facing simulation took (in ms). Populated by step 5
  // and consumed by the plan-picker reveal at step 6 to drive the "it took
  // you Xs / at that rate…" framing.
  const [simulationMs, setSimulationMs] = useState(0);
  // Step layout: 0 intro · 1 hours · 2 goal · 3 niche · 4 price · 5 simulation · 6 plan-picker
  const TOTAL = 7;

  // Fire a "step viewed" event whenever the step changes.
  useEffect(() => {
    logAnalyticsEvent('onboarding_step_viewed', { step, total: TOTAL });
  }, [step]);

  // Lock body scroll while the full-screen funnel is mounted so the page
  // beneath cannot be scrolled through the overlay on iOS Safari / Android.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const go = (next: number) => {
    haptic(10);
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  const answer = <K extends keyof FunnelAnswers>(key: K, value: FunnelAnswers[K], nextStep: number, event: string, payload: Record<string, unknown> = {}) => {
    haptic([8, 18, 8]);
    setAnswers((a) => ({ ...a, [key]: value }));
    logAnalyticsEvent(event, payload);
    setDirection(1);
    setStep(nextStep);
  };

  // Derived target numbers - only meaningful once hours + price have been picked.
  const closesPerMonth = answers.hours ? HOURS_OPTIONS.find((o) => o.id === answers.hours)!.closes : 0;
  const pricePerSite = answers.price ?? 500;
  const projectedMonthly = closesPerMonth * pricePerSite;
  const projectedAnnual = projectedMonthly * 12;

  const slideVariants = {
    enter: (d: number) => ({ x: d > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit:  (d: number) => ({ x: d > 0 ? -40 : 40, opacity: 0 }),
  };

  // Steps 5 (simulation) and 6 (reveal) are one-way: going back would re-run the
  // sim from scratch. Step 0 has nothing behind it.
  const canGoBack = step > 0 && step < 5;

  const progressPct = Math.min(100, Math.round(((step + 1) / TOTAL) * 100));

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* Ambient background glow to match the mobile landing aesthetic. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(91,58,58,0.32) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at bottom, rgba(91,58,58,0.18) 0%, rgba(0,0,0,0) 60%)',
        }}
      />

      {/* Top chrome - back/close + progress bar. Extra top padding + safe
          area inset so the chrome clears the notch on any device. */}
      <div
        className="relative z-10 flex items-center gap-3 px-5 pb-4"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 20px)' }}
      >
        {/* MOBILE HAS NO ESCAPE HATCH (2026-07-24).
            On a phone the funnel opens over the landing page, so "close" would
            dismiss to a page the user was never meant to see and that no longer
            sells anything. The X is therefore rendered on DESKTOP ONLY, where
            there is a real marketing page underneath to close back to.

            Back still works on both, wherever back is meaningful (steps 1-4).
            Steps 5 and 6 are one-way, so on mobile the chrome renders an inert
            spacer there instead - it keeps the progress bar in the same place
            rather than letting it jump left on the last two steps. */}
        {canGoBack ? (
          <button
            onClick={() => {
              haptic(8);
              logAnalyticsEvent('onboarding_back_clicked', { from_step: step });
              go(Math.max(0, step - 1));
            }}
            className="w-9 h-9 rounded-full border border-white/10 bg-white/[0.04] flex items-center justify-center text-gray-400 active:bg-white/[0.08]"
            aria-label="Back"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
          </button>
        ) : isMobile ? (
          <div className="w-9 h-9 flex-shrink-0" aria-hidden="true" />
        ) : (
          <button
            onClick={() => {
              haptic(8);
              logAnalyticsEvent('onboarding_closed', { at_step: step });
              onClose(step);
            }}
            className="w-9 h-9 rounded-full border border-white/10 bg-white/[0.04] flex items-center justify-center text-gray-400 active:bg-white/[0.08]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-primary-500 to-primary-300"
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>

        <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-gray-500 tabular-nums">
          {Math.min(step + 1, TOTAL)}/{TOTAL}
        </span>
      </div>

      {/* Scrollable step body. Uses dynamic viewport height (dvh) where
          supported to avoid the 100vh / iOS url-bar bug. Generous bottom
          padding + safe-area inset ensures the Continue button never sits
          against the edge on small devices. Inner wrapper centers content
          on desktop - funnel was originally mobile-only so the step bodies
          are max-w-sm, and without a centering wrapper they hug the left. */}
      <div
        className="relative z-10 px-6 md:px-10 pt-6 overflow-y-auto overscroll-contain"
        style={{
          height: 'calc(100dvh - 80px)',
          paddingBottom: 'max(env(safe-area-inset-bottom), 40px)',
        }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="min-h-full flex flex-col items-center mx-auto w-full max-w-md md:max-w-2xl"
          >
            {step === 0 && (
              <FunnelIntro
                onGo={() => {
                  logAnalyticsEvent('onboarding_intro_continued', {});
                  setDirection(1);
                  setStep(1);
                }}
              />
            )}

            {step === 1 && (
              <FunnelHoursTimeline
                onPick={(id) => {
                  const match = HOURS_OPTIONS.find((o) => o.id === id);
                  answer('hours', id, 2, 'onboarding_hours_answered', {
                    hours: id,
                    closes: match?.closes ?? 0,
                  });
                }}
              />
            )}

            {step === 2 && (
              <FunnelGoalStep
                onPick={(id) =>
                  answer('goal', id, 3, 'onboarding_goal_answered', { goal: id })
                }
              />
            )}

            {step === 3 && (
              <FunnelNicheStep
                onPick={(id) =>
                  answer('niche', id, 4, 'onboarding_niche_answered', { niche: id })
                }
              />
            )}

            {step === 4 && (
              <FunnelQuestion
                eyebrow="Question 4 of 4"
                title="What feels fair to charge per website?"
                subtitle="A local shop pays thousands a year just for Google Ads. A one-time website is a no-brainer."
                options={PRICE_OPTIONS.map((o) => ({
                  id: String(o.id),
                  label: o.label,
                  sub: o.sub,
                  onSelect: () =>
                    answer('price', o.id, 5, 'onboarding_price_answered', { price: o.id }),
                }))}
              />
            )}

            {/* The ONLY per-platform branch in the funnel. Mobile runs the
                phone-specific walkthrough; desktop keeps the legacy sim
                byte-for-byte. Both report the same elapsed ms to the same
                event, so the two remain directly comparable in analytics. */}
            {step === 5 && (
              isMobile ? (
                <MobileValueReveal
                  answers={answers}
                  onNiche={(id) => setAnswers((a) => ({ ...a, niche: id }))}
                  onContinue={(elapsedMs) => {
                    setSimulationMs(elapsedMs);
                    logAnalyticsEvent('onboarding_simulation_completed', {
                      elapsed_ms: Math.round(elapsedMs),
                      surface: 'mobile',
                    });
                    setDirection(1);
                    setStep(6);
                  }}
                />
              ) : (
                <FunnelSimulation
                  answers={answers}
                  onContinue={(elapsedMs) => {
                    setSimulationMs(elapsedMs);
                    logAnalyticsEvent('onboarding_simulation_completed', {
                      elapsed_ms: Math.round(elapsedMs),
                      surface: 'desktop',
                    });
                    setDirection(1);
                    setStep(6);
                  }}
                />
              )
            )}

            {step === 6 && (
              <FunnelPlanPicker
                answers={answers}
                closesPerMonth={closesPerMonth}
                pricePerSite={pricePerSite}
                simulationMs={simulationMs}
                loading={loading}
                isMobile={isMobile}
                onCheckout={(pickedPlanId) => {
                  logAnalyticsEvent('onboarding_checkout_clicked', {
                    plan_id: pickedPlanId,
                    projected_monthly: projectedMonthly,
                    projected_annual: projectedAnnual,
                    simulation_ms: Math.round(simulationMs),
                  });
                  // User has committed - clear the persisted funnel so they
                  // aren't resumed into a stale picker next time.
                  try { localStorage.removeItem(FUNNEL_STORAGE_KEY); } catch { /* noop */ }
                  onCheckout(pickedPlanId, projectedMonthly);
                }}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ─── FunnelIntro ──────────────────────────────────────────────────────────
// Opening step, rendered as a CLI terminal to match the landing page's
// LiveDemoWindow / MobileLiveTicker aesthetic. Lines stream in one-by-one
// like boot output, ending with a blinking prompt before the user taps
// "Calculate income". Sets the whole "this is a real tool, not a pitch page" tone
// before the first question.
function FunnelIntro({ onGo }: { onGo: () => void }) {
  const [visibleLines, setVisibleLines] = useState(0);

  const lines: { icon: string; color: string; text: string }[] = [
    { icon: '▶', color: 'text-gray-400',  text: 'initializing income plan…'         },
    { icon: '✓', color: 'text-green-400', text: '5 questions · ~60 seconds'         },
    { icon: '✓', color: 'text-green-400', text: 'personalized to your numbers'      },
    { icon: '$', color: 'text-primary-300', text: 'ready to build your plan?'       },
  ];

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i <= lines.length; i++) {
      timers.push(setTimeout(() => setVisibleLines(i), 180 + i * 260));
    }
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allLinesShown = visibleLines >= lines.length;

  return (
    <div className="flex-1 flex flex-col items-center justify-center pt-4 pb-16">
      <p className="text-primary-400 text-[11px] font-semibold tracking-[0.25em] uppercase mb-3">
        60-Second Plan
      </p>
      <h2 className="text-[28px] font-bold text-white leading-[1.15] mb-6 text-center max-w-[19rem]">
        Let's build you a real income plan.
      </h2>

      {/* CLI terminal window - same chrome as the landing page ticker */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm rounded-xl border border-white/10 bg-black/70 backdrop-blur-sm overflow-hidden mb-8"
        style={{ boxShadow: '0 0 60px rgba(91,58,58,0.28)' }}
      >
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/[0.03]">
          <div className="w-2 h-2 rounded-full bg-red-500/60" />
          <div className="w-2 h-2 rounded-full bg-yellow-500/60" />
          <div className="w-2 h-2 rounded-full bg-green-500/60" />
          <span className="text-[10px] text-gray-500 ml-2 font-mono tracking-wide">romulus.live</span>
          <span className="ml-auto text-[10px] text-green-400 font-mono flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            LIVE
          </span>
        </div>

        {/* Terminal body */}
        <div className="p-4 font-mono text-[11px] space-y-1.5 min-h-[160px]">
          {lines.map((line, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 transition-all duration-300 ${
                i < visibleLines ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
              }`}
            >
              <span className={`${line.color} flex-shrink-0 w-3`}>{line.icon}</span>
              <span className="text-gray-300 leading-relaxed">{line.text}</span>
            </div>
          ))}

          {/* Blinking cursor once all lines are visible */}
          {allLinesShown && (
            <div className="flex items-center gap-2 pt-1.5">
              <span className="text-gray-600 font-mono text-[11px]">&gt;</span>
              <BlinkingCursor
                className="inline-block w-[7px] h-[13px] bg-primary-400"
                style={{ transform: 'translateY(1px)' }}
              />
            </div>
          )}
        </div>
      </motion.div>

      <Button size="lg" onClick={onGo} className="w-full max-w-sm h-14 text-base group">
        <span className="flex items-center justify-center gap-2">
          Calculate income
          <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
        </span>
      </Button>
    </div>
  );
}

interface FunnelOption {
  id: string;
  label: string;
  sub: string;
  onSelect: () => void;
}

function FunnelQuestion({
  eyebrow,
  title,
  subtitle,
  options,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  options: FunnelOption[];
}) {
  return (
    <div className="flex-1 flex flex-col pt-4">
      <p className="text-primary-400 text-[10px] font-semibold tracking-[0.25em] uppercase mb-2.5">
        {eyebrow}
      </p>
      <h2 className="text-[26px] font-bold text-white leading-[1.15] mb-3 max-w-[19rem]">
        {title}
      </h2>
      <p className="text-[14px] text-gray-400 leading-relaxed mb-6 max-w-sm">
        {subtitle}
      </p>

      <motion.div
        className="space-y-2.5"
        initial="hidden"
        animate="visible"
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } } }}
      >
        {options.map((opt) => (
          <motion.button
            key={opt.id}
            variants={{ hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={opt.onSelect}
            className="w-full flex items-center justify-between px-5 py-4 rounded-xl border border-white/10 bg-white/[0.03] active:bg-primary-500/10 active:border-primary-500/50 transition-colors text-left"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="flex-1 min-w-0 pr-3">
              <div className="text-white font-semibold text-[15px] leading-tight">{opt.label}</div>
              <div className="text-gray-500 text-[12px] mt-0.5">{opt.sub}</div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
          </motion.button>
        ))}
      </motion.div>
    </div>
  );
}

// ─── FunnelHoursTimeline ──────────────────────────────────────────────────
// Hours step reimagined as a vertical timeline that mirrors the landing
// page's 4-stop workflow graphic. Each commitment level is a timeline node
// with an icon, label, and connector line - tapping any node selects it
// and advances. Uses progression icons (Moon → Sun → Briefcase → Rocket)
// to visually tell the "dip in → go full send" story.
function FunnelHoursTimeline({ onPick }: { onPick: (id: HoursId) => void }) {
  const [selected, setSelected] = useState<HoursId | null>(null);

  const stops: {
    id: HoursId;
    Icon: typeof Moon;
    label: string;
    body: React.ReactNode;
  }[] = [
    {
      id: '2-3',
      Icon: Moon,
      label: '2–3 hrs / week',
      body: <>Evenings only · <span className="text-white font-semibold">first sale in ~2 weeks</span></>,
    },
    {
      id: '4-6',
      Icon: Sun,
      label: '4–6 hrs / week',
      body: <>Weekends · <span className="text-white font-semibold">real side income</span></>,
    },
    {
      id: '7-10',
      Icon: Briefcase,
      label: '7–10 hrs / week',
      body: <>Serious pace · <span className="text-white font-semibold">quit-the-day-job territory</span></>,
    },
    {
      id: '10+',
      Icon: Rocket,
      label: '10+ hrs / week',
      body: <>Full send · <span className="text-white font-semibold">going all in</span></>,
    },
  ];

  const handlePick = (id: HoursId) => {
    haptic([8, 18, 8]);
    setSelected(id);
    setTimeout(() => onPick(id), 260);
  };

  return (
    <div className="flex-1 flex flex-col pt-4">
      <p className="text-primary-400 text-[10px] font-semibold tracking-[0.25em] uppercase mb-2.5">
        Question 1 of 4
      </p>
      <h2 className="text-[26px] font-bold text-white leading-[1.15] mb-3 max-w-[19rem]">
        How many hours can you actually commit?
      </h2>
      <p className="text-[14px] text-gray-400 leading-relaxed mb-7 max-w-sm">
        Be honest. Romulus scales to whatever you've got. Pick the pace that feels sustainable.
      </p>

      <motion.div
        className="w-full max-w-sm"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
        }}
      >
        {stops.map(({ id, Icon, label, body }, i) => {
          const isSelected = selected === id;
          const isLast = i === stops.length - 1;
          return (
            <motion.button
              key={id}
              onClick={() => handlePick(id)}
              variants={{
                hidden: { opacity: 0, x: -14 },
                visible: { opacity: 1, x: 0 },
              }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full flex items-start gap-4 text-left group pb-5"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {/* Icon node + connector line */}
              <div className="relative flex-shrink-0 flex flex-col items-center">
                <motion.div
                  animate={{
                    scale: isSelected ? 1.08 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                  className={`relative w-12 h-12 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'border-primary-400 bg-primary-500/20'
                      : 'border-primary-500/40 bg-primary-500/[0.08] group-active:border-primary-400 group-active:bg-primary-500/15'
                  }`}
                  style={{
                    boxShadow: isSelected
                      ? '0 0 40px rgba(91,58,58,0.8), 0 0 80px rgba(91,58,58,0.4)'
                      : '0 0 20px rgba(91,58,58,0.25)',
                  }}
                >
                  <Icon
                    className={`w-5 h-5 transition-colors ${
                      isSelected ? 'text-primary-200' : 'text-primary-300'
                    }`}
                  />
                </motion.div>
                {!isLast && (
                  <div className="w-[2px] flex-1 mt-1 mb-1 bg-gradient-to-b from-primary-500/40 to-primary-500/10" style={{ minHeight: '24px' }} />
                )}
              </div>

              {/* Label + body */}
              <div className="flex-1 min-w-0 pt-1.5">
                <div className="flex items-center gap-2 mb-1">
                  <p
                    className={`text-[15px] font-bold transition-colors ${
                      isSelected ? 'text-white' : 'text-gray-200'
                    }`}
                  >
                    {label}
                  </p>
                  <ArrowRight className={`w-3.5 h-3.5 transition-colors ${isSelected ? 'text-primary-300' : 'text-gray-600'}`} />
                </div>
                <p className="text-[12px] text-gray-400 leading-snug">{body}</p>
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}

// ─── FunnelNicheStep ──────────────────────────────────────────────────────
// Radial 6-wedge selector. Each niche is a 60° pie slice in an SVG circle
// (with a small black center hole for a donut effect). Tapping a slice
// selects it, glows the wedge, and advances the funnel. Icon + label for
// each niche are overlaid on top of the wedge at the angular midpoint.
// `bare` strips the question chrome (eyebrow / heading / instruction) and
// renders the wheel alone. The mobile walkthrough embeds this inside its own
// coached beat, which supplies its own copy - without `bare` the user would
// read "Question 3 of 4" in the middle of a simulation that has no questions.
function FunnelNicheStep({ onPick, bare = false }: { onPick: (id: NicheId) => void; bare?: boolean }) {
  const [selected, setSelected] = useState<NicheId | null>(null);

  const SIZE = 280;                 // outer diameter
  const R_OUTER = SIZE / 2;         // outer radius
  const R_INNER = 38;               // donut hole radius
  const LABEL_R = R_OUTER * 0.68;   // where icons / labels sit
  const CX = R_OUTER;
  const CY = R_OUTER;

  const handlePick = (id: NicheId) => {
    haptic([8, 18, 8]);
    setSelected(id);
    setTimeout(() => onPick(id), 280);
  };

  // Build each wedge's SVG path (annular sector) + label position.
  const wedges = NICHE_OPTIONS.map((opt, i) => {
    // Start each slice at -90° + i*60° so slice 0 starts at the top.
    const startDeg = -90 + i * 60;
    const endDeg   = -90 + (i + 1) * 60;
    const startRad = (startDeg * Math.PI) / 180;
    const endRad   = (endDeg   * Math.PI) / 180;
    const midRad   = (startRad + endRad) / 2;

    const xOuterStart = CX + R_OUTER * Math.cos(startRad);
    const yOuterStart = CY + R_OUTER * Math.sin(startRad);
    const xOuterEnd   = CX + R_OUTER * Math.cos(endRad);
    const yOuterEnd   = CY + R_OUTER * Math.sin(endRad);
    const xInnerStart = CX + R_INNER * Math.cos(endRad);
    const yInnerStart = CY + R_INNER * Math.sin(endRad);
    const xInnerEnd   = CX + R_INNER * Math.cos(startRad);
    const yInnerEnd   = CY + R_INNER * Math.sin(startRad);

    // Annular sector path: outer arc forward, inner arc backward.
    const d = [
      `M ${xOuterStart},${yOuterStart}`,
      `A ${R_OUTER},${R_OUTER} 0 0,1 ${xOuterEnd},${yOuterEnd}`,
      `L ${xInnerStart},${yInnerStart}`,
      `A ${R_INNER},${R_INNER} 0 0,0 ${xInnerEnd},${yInnerEnd}`,
      'Z',
    ].join(' ');

    const labelX = CX + LABEL_R * Math.cos(midRad);
    const labelY = CY + LABEL_R * Math.sin(midRad);

    return { ...opt, d, labelX, labelY };
  });

  return (
    <div className={bare ? 'flex-1 flex flex-col' : 'flex-1 flex flex-col pt-4'}>
      {!bare && (
        <>
          <p className="text-primary-400 text-[10px] font-semibold tracking-[0.25em] uppercase mb-2.5">
            Question 3 of 4
          </p>
          <h2 className="text-[26px] font-bold text-white leading-[1.15] mb-3 max-w-[19rem]">
            Which local trade sounds easiest to pitch?
          </h2>
          <p className="text-[14px] text-gray-400 leading-relaxed mb-6 max-w-sm">
            Tap a slice. Romulus will start hunting this niche in your area tonight.
          </p>
        </>
      )}

      {/* Radial selector wrapper - centered so the circle sits mid-screen */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative mx-auto"
        style={{ width: SIZE, height: SIZE }}
      >
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="overflow-visible"
        >
          {/* Outer ambient glow ring */}
          <circle
            cx={CX}
            cy={CY}
            r={R_OUTER + 4}
            fill="none"
            stroke="rgba(91,58,58,0.22)"
            strokeWidth={1}
          />

          {wedges.map((w, i) => {
            const isSelected = selected === w.id;
            return (
              <motion.path
                key={w.id}
                d={w.d}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.08 + i * 0.06 }}
                fill={isSelected ? 'rgba(176,128,128,0.35)' : 'rgba(255,255,255,0.025)'}
                stroke={isSelected ? 'rgba(196,148,148,0.9)' : 'rgba(255,255,255,0.1)'}
                strokeWidth={1.5}
                onClick={() => handlePick(w.id)}
                className="cursor-pointer transition-colors"
                style={{
                  filter: isSelected
                    ? 'drop-shadow(0 0 18px rgba(91,58,58,0.85))'
                    : 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
              />
            );
          })}

          {/* Center hub (decorative) */}
          <circle
            cx={CX}
            cy={CY}
            r={R_INNER - 2}
            fill="#000"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={1.5}
          />
          <circle cx={CX} cy={CY} r={5} fill="rgba(176,128,128,0.95)">
            <animate attributeName="r" values="5;7;5" dur="2.5s" repeatCount="indefinite" />
          </circle>
        </svg>

        {/* Icon + label overlays - absolutely positioned in the same coord
            system as the SVG. pointer-events-none so taps fall through to
            the underlying wedge path. */}
        {wedges.map((w) => {
          const Icon = w.Icon;
          const isSelected = selected === w.id;
          return (
            <div
              key={w.id}
              className="absolute flex flex-col items-center gap-1 pointer-events-none"
              style={{
                left: w.labelX,
                top: w.labelY,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Icon
                className={`w-5 h-5 transition-colors ${
                  isSelected ? 'text-primary-100' : 'text-primary-300'
                }`}
              />
              <span
                className={`text-[10px] font-semibold whitespace-nowrap transition-colors ${
                  isSelected ? 'text-white' : 'text-gray-300'
                }`}
              >
                {w.label}
              </span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
}

// ─── FunnelGoalStep ───────────────────────────────────────────────────────
// "What would change your life?" step, reimagined as an interactive bar
// chart. Each goal is a vertical bar whose height scales with its amount -
// tapping a bar selects it and advances the funnel. Mirrors the in-app
// EarningsPage bar-chart vibe (gradient fill, dashed grid, same primary
// color family) so the funnel feels like a preview of the real product.
function FunnelGoalStep({ onPick }: { onPick: (id: GoalId) => void }) {
  const [selected, setSelected] = useState<GoalId | null>(null);
  const maxAmount = Math.max(...GOAL_OPTIONS.map((g) => g.amount));

  const handlePick = (id: GoalId) => {
    haptic([8, 18, 8]);
    setSelected(id);
    // Slight delay so the selected state can breathe before the next step.
    setTimeout(() => onPick(id), 280);
  };

  return (
    <div className="flex-1 flex flex-col pt-4">
      <p className="text-primary-400 text-[10px] font-semibold tracking-[0.25em] uppercase mb-2.5">
        Question 2 of 4
      </p>
      <h2 className="text-[26px] font-bold text-white leading-[1.15] mb-3 max-w-[19rem]">
        What would actually change your life?
      </h2>
      <p className="text-[14px] text-gray-400 leading-relaxed mb-7 max-w-sm">
        Tap the bar that makes you exhale. This is your goal, not a promise. We'll help you work toward it.
      </p>

      {/* Chart container - mirrors EarningsPage styling */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-6"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-primary-300">
            Pick your monthly goal
          </p>
          <span className="text-[10px] text-gray-500 font-mono">tap to select</span>
        </div>

        {/* Dashed grid backdrop */}
        <div
          className="relative h-[200px] flex items-end gap-3 px-1"
          style={{
            backgroundImage:
              'linear-gradient(to top, transparent 0%, transparent calc(25% - 1px), rgba(255,255,255,0.05) 25%, transparent calc(25% + 1px), transparent calc(50% - 1px), rgba(255,255,255,0.05) 50%, transparent calc(50% + 1px), transparent calc(75% - 1px), rgba(255,255,255,0.05) 75%, transparent calc(75% + 1px))',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {GOAL_OPTIONS.map((g, i) => {
            const isSelected = selected === g.id;
            const heightPct = (g.amount / maxAmount) * 100;
            return (
              <button
                key={g.id}
                onClick={() => handlePick(g.id)}
                className="flex-1 flex flex-col items-center justify-end h-full group"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <motion.div
                  initial={{ height: '0%', opacity: 0.3 }}
                  animate={{
                    height: `${heightPct}%`,
                    opacity: 1,
                    scale: isSelected ? 1.04 : 1,
                  }}
                  transition={{
                    height: { duration: 0.8, delay: 0.15 + i * 0.1, ease: [0.16, 1, 0.3, 1] },
                    scale: { duration: 0.2 },
                  }}
                  className={`w-full rounded-t-md transition-colors ${
                    isSelected
                      ? 'bg-gradient-to-t from-primary-500 to-primary-300'
                      : 'bg-gradient-to-t from-primary-500/40 to-primary-300/60 group-active:from-primary-500/70 group-active:to-primary-300/80'
                  }`}
                  style={{
                    boxShadow: isSelected
                      ? '0 0 30px rgba(91,58,58,0.9), 0 0 60px rgba(91,58,58,0.5)'
                      : '0 0 15px rgba(91,58,58,0.3)',
                  }}
                />
              </button>
            );
          })}
        </div>

        {/* X-axis labels - tappable too so the whole column is a hit target */}
        <div className="flex items-start gap-3 px-1 mt-3 pt-3 border-t border-white/5">
          {GOAL_OPTIONS.map((g) => {
            const isSelected = selected === g.id;
            return (
              <button
                key={g.id}
                onClick={() => handlePick(g.id)}
                className="flex-1 text-center"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <p
                  className={`text-[13px] font-bold tabular-nums leading-tight transition-colors ${
                    isSelected ? 'text-white' : 'text-gray-300'
                  }`}
                >
                  ${g.amount >= 1000 ? `${g.amount / 1000}k` : g.amount}
                </p>
                <p className="text-[9px] text-gray-500 mt-0.5 leading-tight">{g.sub}</p>
              </button>
            );
          })}
        </div>
      </motion.div>

    </div>
  );
}

// ─── FunnelParticles ──────────────────────────────────────────────────────
// Three.js particle field scoped to the Ready card. Uses the same
// ParticleField component as the landing page HeroBackground, just rendered
// in its own <Canvas> so we don't have to fight through the funnel overlay
// to see the top-level one.

// Blinking block cursor - hard on/off rather than the soft fade of
// Tailwind's animate-pulse, to match the feel of a real CLI prompt.
function BlinkingCursor({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <motion.span
      aria-hidden
      className={className}
      style={style}
      animate={{ opacity: [1, 1, 0, 0] }}
      transition={{ duration: 1, repeat: Infinity, times: [0, 0.5, 0.5, 1], ease: 'linear' }}
    />
  );
}

// ─── CardCountdown ────────────────────────────────────────────────────────
// Tiny radial timer rendered beside each card's eyebrow during the reveal
// sequence. The ring empties from full → zero over the card's dwell time,
// giving users a visual cue that the next rectangle is about to appear.



// Recharts area chart that mirrors the real product's EarningsPage visual
// language (gradient fill, dashed grid, #B08080 stroke, dark tooltip) so the
// projection screen feels like a preview of the actual dashboard the user
// is about to unlock. Curve is a conservative learning ramp: month 1 at
// ~30%, steady state by month 7 - keeps the graph emotionally compelling
// without implying instant max earnings.

// ─── FunnelSimulation ─────────────────────────────────────────────────────
// Step 5 of the funnel. Runs like an in-game tutorial: four interactive
// stages where the user clicks highlighted targets while a coach bubble
// explains what's happening, followed by a timer reveal and a side-by-side
// race vs. traditional tools. Every stage is framed as "Let's practice"
// so there's no doubt it's an illustrative walkthrough.
// Per-niche mock lead data. Each niche has 6 business names that feel
// plausible for that trade, so downstream copy isn't always "plumbers"
// regardless of what the user selects in the campaign step. Locations are
// overridden at render time to match the selected city.
const NICHE_MOCK_LEADS: Record<string, { name: string; rating: number; reviewCount: number; hasEmail: boolean; hasPhone: boolean }[]> = {
  Plumbers: [
    { name: 'Riverstone Plumbing Co',   rating: 4.8, reviewCount: 142, hasEmail: true,  hasPhone: true },
    { name: 'Clearwater Drain Services', rating: 4.6, reviewCount: 87,  hasEmail: false, hasPhone: true },
    { name: 'Bay Area Pipeworks',        rating: 4.9, reviewCount: 218, hasEmail: true,  hasPhone: true },
    { name: 'Sunset Plumbing Solutions', rating: 4.5, reviewCount: 64,  hasEmail: true,  hasPhone: false },
    { name: 'Pacific Rooter',            rating: 4.7, reviewCount: 103, hasEmail: false, hasPhone: true },
    { name: 'Golden Gate Plumbers',      rating: 4.4, reviewCount: 56,  hasEmail: true,  hasPhone: true },
  ],
  Electricians: [
    { name: 'Riverstone Electric Co',      rating: 4.8, reviewCount: 131, hasEmail: true,  hasPhone: true },
    { name: 'Clearwater Power Services',   rating: 4.6, reviewCount: 74,  hasEmail: false, hasPhone: true },
    { name: 'Suncoast Wiring Solutions',   rating: 4.9, reviewCount: 206, hasEmail: true,  hasPhone: true },
    { name: 'Palm Bay Electric Pros',      rating: 4.7, reviewCount: 92,  hasEmail: true,  hasPhone: false },
    { name: 'Coastal Current Electric',    rating: 4.5, reviewCount: 61,  hasEmail: false, hasPhone: true },
    { name: 'Meridian Electrical Works',   rating: 4.4, reviewCount: 49,  hasEmail: true,  hasPhone: true },
  ],
  'HVAC techs': [
    { name: 'Arctic Air HVAC',             rating: 4.8, reviewCount: 128, hasEmail: true,  hasPhone: true },
    { name: 'Climate Control Experts',     rating: 4.6, reviewCount: 81,  hasEmail: false, hasPhone: true },
    { name: 'Breeze Heating & Cooling',    rating: 4.9, reviewCount: 214, hasEmail: true,  hasPhone: true },
    { name: 'Thermal Dynamics HVAC',       rating: 4.5, reviewCount: 58,  hasEmail: true,  hasPhone: false },
    { name: 'Comfort Zone Systems',        rating: 4.7, reviewCount: 97,  hasEmail: false, hasPhone: true },
    { name: 'Meridian Air Solutions',      rating: 4.4, reviewCount: 52,  hasEmail: true,  hasPhone: true },
  ],
  Landscapers: [
    { name: 'Greenline Landscaping',       rating: 4.8, reviewCount: 119, hasEmail: true,  hasPhone: true },
    { name: 'Stoneridge Lawn Care',        rating: 4.6, reviewCount: 68,  hasEmail: false, hasPhone: true },
    { name: 'Cedar & Sage Gardens',        rating: 4.9, reviewCount: 187, hasEmail: true,  hasPhone: true },
    { name: 'Horizon Outdoor Design',      rating: 4.5, reviewCount: 54,  hasEmail: true,  hasPhone: false },
    { name: 'Pacific Greenkeepers',        rating: 4.7, reviewCount: 88,  hasEmail: false, hasPhone: true },
    { name: 'Meadow Run Landscaping',      rating: 4.4, reviewCount: 44,  hasEmail: true,  hasPhone: true },
  ],
  Roofers: [
    { name: 'Summit Roofing Co',           rating: 4.8, reviewCount: 122, hasEmail: true,  hasPhone: true },
    { name: 'Ironclad Roofers',            rating: 4.6, reviewCount: 79,  hasEmail: false, hasPhone: true },
    { name: 'Skyline Shingles & Slate',    rating: 4.9, reviewCount: 198, hasEmail: true,  hasPhone: true },
    { name: 'Redwood Ridge Roofing',       rating: 4.5, reviewCount: 61,  hasEmail: true,  hasPhone: false },
    { name: 'Pacific Crest Roof Pros',     rating: 4.7, reviewCount: 91,  hasEmail: false, hasPhone: true },
    { name: 'Meridian Roofworks',          rating: 4.4, reviewCount: 47,  hasEmail: true,  hasPhone: true },
  ],
  Cleaners: [
    { name: 'Spotless Pro Cleaning',       rating: 4.8, reviewCount: 115, hasEmail: true,  hasPhone: true },
    { name: 'Crystal Clean Services',      rating: 4.6, reviewCount: 72,  hasEmail: false, hasPhone: true },
    { name: 'Pristine Home & Office',      rating: 4.9, reviewCount: 184, hasEmail: true,  hasPhone: true },
    { name: 'Meridian Maids',              rating: 4.5, reviewCount: 56,  hasEmail: true,  hasPhone: false },
    { name: 'Pacific Shine Cleaning',      rating: 4.7, reviewCount: 88,  hasEmail: false, hasPhone: true },
    { name: 'Goldleaf Janitorial',         rating: 4.4, reviewCount: 41,  hasEmail: true,  hasPhone: true },
  ],
};

function FunnelSimulation({ answers, onContinue }: {
  answers: FunnelAnswers;
  onContinue: (elapsedMs: number) => void;
}) {
  type Stage =
    | 'intro'
    | 'campaign_dialog'
    | 'campaign_list'
    | 'scraping'
    | 'leads'
    | 'template'
    | 'features'
    | 'customize'
    | 'site_gen'
    | 'sites_page'
    | 'outreach'
    | 'reveal'
    | 'race'
    | 'payout';
  const [stage, setStage] = useState<Stage>('intro');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [finalMs, setFinalMs] = useState<number | null>(null);

  // Selected values from the Campaign step - threaded through every
  // downstream stage so the sim reflects what the user actually picked.
  const seedNiche = answers.niche
    ? (NICHE_OPTIONS.find((o) => o.id === answers.niche)?.label ?? 'Plumbers')
    : 'Plumbers';
  const [campNiche, setCampNiche] = useState<string>(NICHE_CHOICES.includes(seedNiche) ? seedNiche : 'Plumbers');
  const [campCity,  setCampCity]  = useState<string>('San Francisco, CA');
  const [campLeads, setCampLeads] = useState<number>(25);
  const [campPrice, setCampPrice] = useState<number>(299);
  const [campFeatures, setCampFeatures] = useState<string[]>([]);

  const timerStart = () => {
    if (startedAt === null) setStartedAt(performance.now());
  };

  const finish = () => {
    const ms = startedAt !== null ? performance.now() - startedAt : 0;
    setFinalMs(ms);
    setStage('reveal');
  };

  // Resolve the top lead for that niche so later stages show a
  // niche-appropriate business name on the sites page / outreach.
  const nicheLeads = NICHE_MOCK_LEADS[campNiche] ?? NICHE_MOCK_LEADS.Plumbers;
  const topBusiness = nicheLeads[0].name;

  return (
    <div className="flex-1 flex flex-col items-center justify-center pt-1 w-full min-h-[85vh]">
      <AnimatePresence mode="wait">
        {stage === 'intro' && (
          <TutorialIntro
            key="intro"
            onStart={() => { timerStart(); setStage('campaign_dialog'); }}
          />
        )}
        {stage === 'campaign_dialog' && (
          <CampaignStage
            key="campaign"
            niche={campNiche}
            onComplete={(n, c, l) => {
              setCampNiche(n); setCampCity(c); setCampLeads(l);
              setStage('campaign_list');
            }}
          />
        )}
        {stage === 'campaign_list' && (
          <CampaignListStage
            key="campaign_list"
            niche={campNiche}
            city={campCity}
            leads={campLeads}
            onNext={() => setStage('scraping')}
          />
        )}
        {stage === 'scraping' && (
          <ScrapingStage
            key="scraping"
            niche={campNiche}
            city={campCity}
            leads={campLeads}
            onNext={() => setStage('leads')}
          />
        )}
        {stage === 'leads' && (
          <LeadsStage
            key="leads"
            niche={campNiche}
            city={campCity}
            leads={campLeads}
            onNext={() => setStage('template')}
          />
        )}
        {stage === 'template' && (
          <TemplateStage
            key="template"
            niche={campNiche}
            business={topBusiness}
            city={campCity}
            onNext={() => setStage('features')}
          />
        )}
        {stage === 'features' && (
          <FeaturesStage
            key="features"
            business={topBusiness}
            onNext={(f) => { setCampFeatures(f); setStage('site_gen'); }}
          />
        )}
        {stage === 'site_gen' && (
          <SiteGenerationStage
            key="site_gen"
            business={topBusiness}
            features={campFeatures}
            onNext={() => setStage('customize')}
          />
        )}
        {stage === 'customize' && (
          <CustomizeStage
            key="customize"
            niche={campNiche}
            business={topBusiness}
            city={campCity}
            onNext={() => setStage('sites_page')}
          />
        )}
        {stage === 'sites_page' && (
          <SitesPageStage
            key="sites_page"
            business={topBusiness}
            city={campCity}
            initialPrice={campPrice}
            onNext={(p) => { setCampPrice(p); setStage('outreach'); }}
          />
        )}
        {stage === 'outreach' && (
          <OutreachStage
            key="outreach"
            business={topBusiness}
            onNext={finish}
          />
        )}
        {stage === 'reveal' && finalMs !== null && (
          <RevealStage
            key="reveal"
            ms={finalMs}
            count={campLeads}
            onNext={() => setStage('payout')}
          />
        )}
        {stage === 'payout' && finalMs !== null && (
          <PayoutFlowStage
            key="payout"
            ms={finalMs}
            price={campPrice}
            onContinue={() => onContinue(finalMs)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Soft practice-mode badge used at the top of every interactive stage.
function PracticeBadge({ stepOf }: { stepOf: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-2 mb-4"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-primary-300 animate-pulse" />
      <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-primary-300">
        Let's practice · {stepOf}
      </span>
    </motion.div>
  );
}

// Coach popup: small card with an arrow that sits above or below the
// spotlighted element and explains what the user should do next.
function Coach({ children, position = 'above' }: {
  children: React.ReactNode;
  position?: 'above' | 'below';
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: position === 'above' ? 6 : -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      className={`relative z-30 w-fit max-w-sm mx-auto ${position === 'above' ? 'mb-3' : 'mt-3'}`}
    >
      <div
        className="rounded-xl border border-primary-400/40 bg-[#1a0d0d] px-4 py-2.5 flex items-center gap-2.5"
        style={{ boxShadow: '0 8px 30px rgba(91,58,58,0.6)' }}
      >
        {/* Mini wolf - messages read as coming from Romulus himself, not a
            generic UI sparkle. Mirrors the sidebar + intro splash treatment. */}
        <div className="w-6 h-6 rounded-md border border-white bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
          <img src="/wolfromulus.png" alt="Romulus" className="w-[18px] h-[18px] object-contain" />
        </div>
        <p className="text-[13px] text-white leading-snug">{children}</p>
      </div>
      {/* Arrow pointing at the target */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-[#1a0d0d] border-primary-400/40 ${
          position === 'above' ? 'bottom-[-6px] border-b border-r' : 'top-[-6px] border-t border-l'
        }`}
      />
    </motion.div>
  );
}

// Animated hand-pointer cursor that eases over to the target, then bounces
// a couple of times to signal "click here". Purely visual - the user still
// clicks the real element underneath.
function TutorialCursor() {
  return (
    <motion.div
      initial={{ x: -40, y: -30, opacity: 0 }}
      animate={{ x: 0, y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="absolute -right-4 -bottom-4 z-40 pointer-events-none"
    >
      <motion.div
        animate={{ scale: [1, 0.88, 1] }}
        transition={{ duration: 0.9, repeat: Infinity, delay: 1.5, ease: 'easeInOut' }}
        className="w-7 h-7 drop-shadow-[0_4px_12px_rgba(0,0,0,0.7)]"
      >
        <svg viewBox="0 0 24 24" fill="white" stroke="black" strokeWidth="1.5">
          <path d="M3 2l5 18 3-7 7-3z" />
        </svg>
      </motion.div>
    </motion.div>
  );
}

// Full-frame dim overlay used behind the spotlighted elements of each
// tutorial stage. The spotlighted element sits at z-index 20+ to pop above.
function DimBackdrop() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="absolute inset-0 bg-black/50 backdrop-blur-[1px] z-10 rounded-2xl"
    />
  );
}

// ─── TutorialIntro ────────────────────────────────────────────────────────
function TutorialIntro({ onStart }: { onStart: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.5 }}
      className="flex-1 flex flex-col items-center justify-center py-10 w-full"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-20 h-20 rounded-2xl border-2 border-white bg-black flex items-center justify-center overflow-hidden mb-8"
        style={{ boxShadow: '0 0 60px rgba(91,58,58,0.5)' }}
      >
        <img src="/wolfromulus.png" alt="Romulus" className="w-16 h-16 object-contain" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25 }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary-500/40 bg-primary-500/[0.08] mb-4"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-primary-300 animate-pulse" />
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary-300">
          Founding member access
        </span>
      </motion.div>
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="text-primary-400 text-[11px] font-semibold tracking-[0.25em] uppercase mb-3"
      >
        60-second walkthrough
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.45 }}
        className="text-[36px] md:text-[48px] font-bold text-white leading-tight text-center max-w-xl mb-3"
      >
        Let's practice making sales.
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.65 }}
        className="text-[15px] text-gray-400 text-center max-w-md leading-relaxed mb-10"
      >
        You'll click through one full Romulus loop - create a campaign, scrape leads, generate a site, send the pitch.
        Everything you do here is the same as what happens in the real product.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9 }}
        className="w-full max-w-md"
      >
        <Button
          size="lg"
          onClick={onStart}
          className="w-full h-14 text-base group"
        >
          <span className="flex items-center justify-center gap-2">
            Start the walkthrough
            <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
          </span>
        </Button>
      </motion.div>
    </motion.div>
  );
}

// Shared window-chrome wrapper used by every interactive stage so the
// tutorial feels like it's happening inside a real app window.
function AppWindow({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="w-full max-w-2xl rounded-2xl border border-white/10 bg-black/80 backdrop-blur-sm overflow-hidden relative"
      style={{ boxShadow: '0 20px 80px rgba(0,0,0,0.6)' }}
    >
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-white/[0.03]">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        <span className="text-[10px] text-gray-500 ml-3 font-mono tracking-wide">{title}</span>
      </div>
      <div className="p-5 md:p-6 relative">{children}</div>
    </div>
  );
}

// ─── CampaignStage (typeable inputs + per-field coach) ────────────────────
// Instead of showing the form pre-filled, the user actually types the niche,
// a city, and a lead count. The coach popup follows the active field so each
// step has its own beat. Commitment-building: once they've typed their own
// answers in, they've hand-crafted the campaign themselves.
const NICHE_CHOICES = ['Plumbers', 'Electricians', 'HVAC techs', 'Landscapers', 'Roofers', 'Cleaners'];
const CITY_CHOICES = ['San Francisco, CA', 'Austin, TX', 'Denver, CO', 'Miami, FL', 'Seattle, WA', 'Phoenix, AZ'];
const LEADS_CHOICES = [10, 25, 50, 100];

function CampaignStage({ niche: nichePrefill, onComplete }: {
  niche: string;
  onComplete: (niche: string, city: string, leads: number) => void;
}) {
  const defaultNiche = NICHE_CHOICES.includes(nichePrefill) ? nichePrefill : 'Plumbers';
  const [niche, setNiche] = useState(defaultNiche);
  const [city, setCity] = useState('');
  const [leads, setLeads] = useState<number | null>(null);

  const active =
    !niche ? 0 :
    !city ? 1 :
    !leads ? 2 : 3;

  const COACH_LINES = [
    <>Pick your niche. We pre-selected based on your earlier answer.</>,
    <>Pick a city to target. Try <span className="text-primary-300 font-semibold">San Francisco, CA</span> for this run.</>,
    <>How many leads per scrape? <span className="text-primary-300 font-semibold">25</span> is a good starter size.</>,
    <>Hit <span className="text-primary-300 font-semibold">Create</span> and Romulus will take it from there.</>,
  ];

  const fieldWrapCls = (idx: number) =>
    active === idx ? 'border-primary-400 bg-primary-500/[0.08] ring-2 ring-primary-400/30'
    : idx < active ? 'border-green-500/30 bg-green-500/[0.04]'
    : 'border-white/10 bg-white/[0.03] opacity-60';

  const Chip = ({ selected, disabled, onClick, children }: {
    selected: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode;
  }) => (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-md text-[12px] font-medium border transition-all whitespace-nowrap ${
        selected
          ? 'bg-primary-500 border-primary-400 text-white'
          : disabled
          ? 'bg-white/[0.02] border-white/5 text-gray-600 cursor-not-allowed'
          : 'bg-white/[0.03] border-white/10 text-gray-300 hover:bg-white/[0.06] hover:border-white/20'
      }`}
    >
      {children}
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 1 of 10" />

      <Coach position="above">{COACH_LINES[active]}</Coach>

      <AppWindow title="romulus · new campaign">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">
          New campaign
        </p>
        <div className="space-y-3">
          <div className={`rounded-lg border p-3 transition-all ${fieldWrapCls(0)}`}>
            <label className="text-[11px] font-semibold text-gray-400 mb-2 block">Niche</label>
            <div className="flex flex-wrap gap-1.5">
              {NICHE_CHOICES.map((n) => (
                <Chip key={n} selected={niche === n} onClick={() => setNiche(n)}>{n}</Chip>
              ))}
            </div>
          </div>
          <div className={`rounded-lg border p-3 transition-all ${fieldWrapCls(1)}`}>
            <label className="text-[11px] font-semibold text-gray-400 mb-2 block">City</label>
            <div className="flex flex-wrap gap-1.5">
              {CITY_CHOICES.map((c) => (
                <Chip key={c} selected={city === c} disabled={active < 1} onClick={() => setCity(c)}>{c}</Chip>
              ))}
            </div>
          </div>
          <div className={`rounded-lg border p-3 transition-all ${fieldWrapCls(2)}`}>
            <label className="text-[11px] font-semibold text-gray-400 mb-2 block">Max leads per scrape</label>
            <div className="flex flex-wrap gap-1.5">
              {LEADS_CHOICES.map((n) => (
                <Chip key={n} selected={leads === n} disabled={active < 2} onClick={() => setLeads(n)}>{n} leads</Chip>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <motion.div
            animate={active === 3 ? { scale: [1, 1.03, 1] } : { scale: 1 }}
            transition={active === 3 ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
            className="relative"
          >
            <button
              onClick={() => {
                if (active === 3 && leads != null) onComplete(niche, city, leads);
              }}
              disabled={active !== 3}
              className={`relative px-5 py-2.5 rounded-lg font-semibold text-[13px] flex items-center gap-2 transition-all ${
                active === 3
                  ? 'bg-gradient-to-r from-primary-500 to-primary-400 text-white'
                  : 'bg-white/[0.04] text-gray-600 cursor-not-allowed'
              }`}
              style={active === 3 ? { boxShadow: '0 0 40px rgba(176,128,128,0.7), 0 0 0 2px rgba(176,128,128,0.3)' } : undefined}
            >
              Create campaign
              <ArrowRight className="w-4 h-4" />
            </button>
            {active === 3 && <TutorialCursor />}
          </motion.div>
        </div>
      </AppWindow>
    </motion.div>
  );
}

// ─── CampaignListStage (new: user clicks "Scrape leads") ──────────────────
function CampaignListStage({ niche, city, leads, onNext }: {
  niche: string; city: string; leads: number; onNext: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 2 of 10" />

      <Coach position="above">
        Campaign created. Now hit <span className="text-primary-300 font-semibold">Scrape leads</span> to find businesses without websites.
      </Coach>

      <AppWindow title="romulus · campaigns">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-3">
          Campaigns tab · all your campaigns live here
        </p>

        <div className="space-y-2">
          {/* Spotlighted - the one they just created */}
          <motion.div
            animate={{ scale: [1, 1.01, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="relative rounded-xl border border-primary-400/60 bg-primary-500/[0.08] p-4"
            style={{ boxShadow: '0 0 30px rgba(176,128,128,0.3)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[14px] font-semibold text-white">Practice Campaign</p>
                <p className="text-[11px] text-gray-400">{niche} · {city} · {leads} leads max</p>
              </div>
              <p className="text-[11px] text-gray-500 tabular-nums">0 / {leads} leads</p>
            </div>

            <div className="flex justify-end">
              <div className="relative">
                <motion.div
                  animate={hovered ? { scale: 1.04 } : { scale: [1, 1.03, 1] }}
                  transition={hovered ? { duration: 0.2 } : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <button
                    onClick={onNext}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-primary-400 text-white font-semibold text-[12px] flex items-center gap-1.5"
                    style={{ boxShadow: '0 0 30px rgba(176,128,128,0.6)' }}
                  >
                    Scrape leads
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </motion.div>
                <TutorialCursor />
              </div>
            </div>
          </motion.div>

          {/* Other mock campaigns so the page doesn't look empty. Dimmed
              and non-interactive. Real users would see their full list
              of prior campaigns here. */}
          <div className="space-y-2 opacity-40 pointer-events-none">
            {[
              { name: 'Denver Roofers Q1', meta: 'Roofers · Denver, CO · 50 leads', count: '47 / 50 leads' },
              { name: 'Austin HVAC Push',  meta: 'HVAC techs · Austin, TX · 100 leads', count: '94 / 100 leads' },
              { name: 'Miami Cleaners',     meta: 'Cleaners · Miami, FL · 25 leads',    count: '25 / 25 leads' },
            ].map((c) => (
              <div key={c.name} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-[14px] font-semibold text-white">{c.name}</p>
                    <p className="text-[11px] text-gray-400">{c.meta}</p>
                  </div>
                  <p className="text-[11px] text-gray-500 tabular-nums">{c.count}</p>
                </div>
                <div className="flex justify-end">
                  <button className="px-4 py-2 rounded-lg border border-white/10 bg-white/[0.03] text-gray-500 font-semibold text-[12px]">
                    Scrape leads
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AppWindow>
    </motion.div>
  );
}

// ─── ScrapingStage (uses real HackerLoadingScreen) ────────────────────────
// Renders the exact same portal-based CLI component the real product uses
// during an actual scrape. We feed it fake but plausible logs, a growing
// foundLeads array, and live stats so the screen looks identical to what
// users will see in-app. After ~5 seconds we dismiss the portal and show a
// Continue affordance outside it.

function ScrapingStage({ niche, city, leads, onNext }: { niche: string; city: string; leads: number; onNext: () => void }) {
  const nicheLeadsRaw = NICHE_MOCK_LEADS[niche] ?? NICHE_MOCK_LEADS.Plumbers;
  // Target is exactly what the user asked for. If they picked more than the
  // mock pool has, cycle the base names with neighborhood suffixes so the
  // list reads like distinct businesses rather than the same 6 on repeat.
  const TARGET = leads;
  const SUFFIXES = ['', ' - Downtown', ' North', ' West', ' II', ' Co', ' South', ' Midtown', ' East', ' Group', ' LLC', ' Pros', ' Express', ' Solutions', ' Group II', ' NW', ' SE', ' Partners'];
  const withCity = Array.from({ length: TARGET }, (_, i) => {
    const base = nicheLeadsRaw[i % nicheLeadsRaw.length];
    const suffix = SUFFIXES[Math.floor(i / nicheLeadsRaw.length) % SUFFIXES.length];
    return { ...base, name: `${base.name}${suffix}`, location: city };
  });

  // HackerLoadingScreen gates lead rendering behind its 3-step init (~1200ms).
  // Start firing leads after that, scale per-lead delay so streaming takes
  // roughly 5s regardless of count, then leave ~1.2s buffer so the user
  // actually sees the last few leads before the auto-advance.
  const perLeadMs = Math.max(30, Math.min(320, 5000 / TARGET));
  const LEAD_START_MS = 1500;
  const TOTAL_MS = LEAD_START_MS + TARGET * perLeadMs + 1200;

  const [found, setFound] = useState<typeof withCity>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const LOG_LINES = [
      `Scanning ${niche} in ${city}`,
      'Querying Google Places API',
      'Filtering out businesses with websites',
      'Enriching contact info',
    ];
    LOG_LINES.forEach((line, i) => {
      timers.push(setTimeout(() => setLogs((prev) => [...prev, line]), 300 + i * 600));
    });
    withCity.forEach((lead, i) => {
      timers.push(setTimeout(() => setFound((prev) => [...prev, lead]), 800 + i * perLeadMs));
    });
    timers.push(setTimeout(onNext, TOTAL_MS));

    const started = performance.now();
    let raf = 0;
    const tick = () => {
      const t = Math.min(1, (performance.now() - started) / TOTAL_MS);
      setProgress(Math.round(t * 100));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [niche, city, leads]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 3 of 10" />
      <Coach position="above">
        Watch Romulus pull <span className="text-primary-300 font-semibold">{TARGET}</span> {niche.toLowerCase().replace(/\bhvac\b/g, 'HVAC')} in {city} with no websites.
      </Coach>
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-black/80 overflow-hidden" style={{ boxShadow: '0 20px 80px rgba(0,0,0,0.6)' }}>
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-white/[0.03]">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          <span className="text-[10px] text-gray-500 ml-3 font-mono tracking-wide">romulus · scraping</span>
        </div>
        <div className="relative" style={{ height: 440 }}>
          <HackerLoadingScreen
            isOpen
            inline
            title="SCRAPING LEADS"
            subtitle={`${niche} in ${city}`}
            mode="scraping"
            logs={logs}
            foundLeads={found}
            currentQuery={niche}
            currentLocation={city}
            stats={{
              businessesFound: found.length,
              businessesWithEmail: found.filter((f) => f.hasEmail).length,
              businessesNoEmail: found.filter((f) => !f.hasEmail).length,
              citiesSearched: 1,
              progress,
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}

// ─── SitesPageStage (new: set price + Send to Business) ───────────────────
function SitesPageStage({ business, city, initialPrice, onNext }: {
  business: string;
  city: string;
  initialPrice: number;
  onNext: (price: number) => void;
}) {
  const [price, setPrice] = useState(String(initialPrice));
  const priceOk = price.trim() && Number(price) >= 50;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 9 of 10" />

      <Coach position="above">
        Site's live. Set your price and send the pitch.
      </Coach>

      <AppWindow title="romulus · sites">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          {/* Site preview tile */}
          <div
            className="h-24 bg-gradient-to-br from-primary-500/20 via-primary-400/10 to-transparent flex items-center justify-center relative"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="absolute top-2 left-2 flex gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500/50" />
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500/50" />
              <div className="w-1.5 h-1.5 rounded-full bg-green-500/50" />
            </div>
            <p className="text-[18px] font-bold text-white tracking-wide">{business}</p>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[13px] font-semibold text-white">{business}</p>
                <p className="text-[10px] text-gray-500">{city} · draft</p>
              </div>
              <span className="px-2 py-0.5 rounded-full bg-primary-500/15 border border-primary-500/30 text-[9px] font-bold tracking-[0.15em] uppercase text-primary-300">
                Ready
              </span>
            </div>

            {/* Price input — user edits */}
            <div className="mb-3">
              <label className="text-[10px] font-semibold text-gray-400 mb-1 block uppercase tracking-wider">
                Your price (USD)
              </label>
              <div className="flex items-center gap-2 rounded-lg border border-primary-400/50 bg-primary-500/[0.05] px-3 py-2 ring-2 ring-primary-400/20">
                <span className="text-[13px] text-gray-500">$</span>
                <input
                  type="number"
                  min={50}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="flex-1 bg-transparent text-[16px] font-bold text-white outline-none tabular-nums"
                />
                <span className="text-[11px] text-gray-500">one-time</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">You keep 100%. Romulus takes no commission.</p>
            </div>

            <div className="relative">
              <motion.div
                animate={priceOk ? { scale: [1, 1.02, 1] } : { scale: 1 }}
                transition={priceOk ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
              >
                <button
                  onClick={() => priceOk && onNext(Number(price))}
                  disabled={!priceOk}
                  className={`w-full py-2.5 rounded-lg font-semibold text-[13px] flex items-center justify-center gap-2 transition-all ${
                    priceOk
                      ? 'bg-gradient-to-r from-primary-500 to-primary-400 text-white'
                      : 'bg-white/[0.04] text-gray-600 cursor-not-allowed'
                  }`}
                  style={priceOk ? { boxShadow: '0 0 30px rgba(176,128,128,0.55)' } : undefined}
                >
                  <Send className="w-4 h-4" />
                  Send to business
                </button>
              </motion.div>
              {priceOk && <TutorialCursor />}
            </div>
          </div>
        </div>
      </AppWindow>
    </motion.div>
  );
}

// ─── PayoutFlowStage (new: money flow animation as sim finale) ────────────
// Final beat of the simulation. Small diagram animates money traveling
// buyer -> Romulus checkout -> seller's Stripe Connect account, landing at
// 100% of the sale. Makes the "who gets paid what" question visible so the
// user stops worrying about Romulus being a middleman taking a cut.
// ─── PayoutFlowStage (redesigned: horizontal earnings bars) ──────────────
// No emojis, no currency-bag icons. Presents the payment flow as a stacked
// sideways bar graph so the user sees at a glance that the full sale price
// lands in their account, not split or clipped. Also shows a comparison
// row against typical commission platforms so the "100% to you" framing
// has visual weight next to the alternative.
// ─── PayoutFlowStage (simple 3-circle money transfer, no emojis, no green) ─
// Business (storefront dot) pays, Romulus (wolf card) runs checkout,
// the full amount lands on You (plain dot). Coins slide between nodes.
// All primary-palette, no green highlights, no emoji characters.
// ─── PayoutFlowStage (correct Stripe-Connect flow) ────────────────────────
// Two sources, one destination. Buyer pays YOU directly via Stripe Connect
// (money never touches Romulus). Romulus separately delivers the site
// files. That's the real architecture and this visualization reflects it.
function FlowLane({
  active, label,
  sourceIcon, sourceLabel, sourceSub,
  targetLabel, targetSub,
  pillText, pillTone,
}: {
  active: boolean; label: string;
  sourceIcon?: string; sourceLabel: string; sourceSub: string;
  targetLabel: string; targetSub: string;
  pillText: string; pillTone: 'primary' | 'muted';
}) {
  const pillCls = pillTone === 'primary'
    ? 'bg-primary-500 border-primary-300 text-white'
    : 'bg-white/90 border-white text-black';
  const pillGlow = pillTone === 'primary'
    ? '0 0 35px rgba(176,128,128,0.7)'
    : '0 0 22px rgba(255,255,255,0.35)';
  const trackGrad = pillTone === 'primary'
    ? 'from-primary-500/10 via-primary-400/60 to-primary-500/10'
    : 'from-white/10 via-white/30 to-white/10';

  const Icon = ({ src, label: l }: { src?: string; label: string }) => (
    <div className="w-20 h-20 rounded-2xl border border-primary-500/40 bg-primary-500/[0.06] flex items-center justify-center overflow-hidden flex-shrink-0">
      {src ? (
        <img src={src} alt={l} className="w-12 h-12 object-contain" />
      ) : (
        <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-primary-300">{l}</span>
      )}
    </div>
  );

  return (
    <div className="grid items-center gap-4" style={{ gridTemplateColumns: '160px 1fr 160px' }}>
      {/* Source node */}
      <div className="flex flex-col items-center gap-2">
        <Icon src={sourceIcon} label={sourceLabel} />
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary-300">{sourceLabel}</p>
        <p className="text-[10px] text-gray-500 text-center leading-snug">{sourceSub}</p>
      </div>

      {/* Arrow track + travelling square · label. Track runs between the two
          nodes; travelling square stops at ~85% so it doesn't collide with
          the arrowhead or target node. */}
      <div className="relative h-20 flex items-center">
        <span className="absolute -top-1 left-1/2 -translate-x-1/2 text-[9px] font-bold tracking-[0.2em] uppercase text-gray-500 whitespace-nowrap">
          {label}
        </span>

        <div className={`relative w-full h-[2px] bg-gradient-to-r ${trackGrad} rounded-full`}>
          {/* Arrow head */}
          <span
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-[2px] w-0 h-0"
            style={{
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              borderLeftWidth: '7px',
              borderLeftStyle: 'solid',
              borderLeftColor: pillTone === 'primary' ? 'rgba(176,128,128,0.8)' : 'rgba(255,255,255,0.3)',
            }}
          />
          {/* Travelling rounded-square pill */}
          <AnimatePresence>
            {active && (
              <motion.div
                key="pill"
                initial={{ left: '0%', opacity: 0 }}
                animate={{ left: '85%', opacity: [0, 1, 1] }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl border px-3 h-10 flex items-center justify-center ${pillCls}`}
                style={{ boxShadow: pillGlow, minWidth: 52 }}
              >
                <span className="text-[11px] font-bold tabular-nums whitespace-nowrap">{pillText}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Target node */}
      <div className="flex flex-col items-center gap-2">
        <Icon label={targetLabel} />
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary-300">{targetLabel}</p>
        <p className="text-[10px] text-gray-500 text-center leading-snug">{targetSub}</p>
      </div>
    </div>
  );
}

function PayoutFlowStage({ ms, price, onContinue }: { ms: number; price: number; onContinue: () => void }) {
  const stopwatch = formatStopwatch(ms);
  // phase 0 idle · 1 money flies buyer→you · 2 files fly romulus→buyer · 3 settled
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 800);
    const t2 = setTimeout(() => setPhase(2), 2000);
    const t3 = setTimeout(() => setPhase(3), 3300);
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="flex-1 flex flex-col items-center justify-center pt-2 w-full min-h-[70vh]"
    >
      <p className="text-primary-400 text-[11px] font-semibold tracking-[0.25em] uppercase mb-3">
        Here's how you'll get paid
      </p>
      <h2 className="text-[26px] md:text-[34px] font-bold text-white leading-tight text-center max-w-2xl mb-3">
        You took <span className="text-primary-300 tabular-nums">{stopwatch}</span>. Romulus takes it from here.
      </h2>
      <p className="text-[14px] text-gray-400 mb-10 text-center max-w-lg">
        Romulus handles checkout and file handoff with the buyer. You get paid direct and never touch hosting.
      </p>

      {/* Two parallel flow lanes. Each lane is a horizontal row of two rounded
          icons with a labelled arrow between them. A pill rides the arrow
          when the phase for that lane fires. */}
      <div className="w-full max-w-3xl mb-8 space-y-8">
        <FlowLane
          active={phase >= 1}
          label="Money · Stripe Connect"
          sourceLabel="Buyer"
          sourceSub={`pays $${price}`}
          targetLabel="You"
          targetSub="receives funds direct"
          pillText={`$${price}`}
          pillTone="primary"
        />
        <FlowLane
          active={phase >= 2}
          label="Files · Romulus"
          sourceIcon="/wolfromulus.png"
          sourceLabel="Romulus"
          sourceSub="runs checkout, ships files"
          targetLabel="Buyer"
          targetSub="receives the site"
          pillText="site files"
          pillTone="muted"
        />
      </div>

      {/* Bottom: zero-commission card */}
      <div className="w-full max-w-xl mb-7 rounded-xl border border-primary-500/30 bg-primary-500/[0.04] p-3.5 text-center">
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-primary-300 mb-1">
          Zero commission
        </p>
        <p className="text-[12px] text-gray-300">
          Romulus is a flat monthly tool. No per-sale fees. No payout delay.
        </p>
      </div>

      <div className="w-full max-w-md">
        <Button size="lg" onClick={onContinue} className="w-full h-14 text-base group">
          <span className="flex items-center justify-center gap-2">
            Show me the numbers
            <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
          </span>
        </Button>
      </div>
    </motion.div>
  );
}

// HH:MM:SS stopwatch formatter. ms → "00:00:24.3".
function formatStopwatch(ms: number): string {
  const totalSecs = ms / 1000;
  const hrs = Math.floor(totalSecs / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  const hrsStr = String(hrs).padStart(2, '0');
  const minsStr = String(mins).padStart(2, '0');
  const secsStr = secs.toFixed(1).padStart(4, '0');
  return `${hrsStr}:${minsStr}:${secsStr}`;
}

// ─── TemplateStage (new: template picker) ────────────────────────────────
// Mock template gallery matching the real ThemeSelectPage layout. Shows 6
// templates in a 3-column grid; the "suggested" card (picked from the
// user's niche in the real app) pulses and carries the Matches-your-niche
// badge. Only the suggested card advances the sim; others can be clicked
// for feel but don't move forward until the user taps the right one.
// Mock template gallery. Each template is a MiniSitePreview with its own
// color theme + layout style so the gallery looks like a real template
// library, not a row of color gradients.
type TemplateTheme = {
  id: string;
  name: string;
  category: string;
  primary: string;
  accent: string;
  bg: string;
  heading: string;  // font-family class
  layout: 'hero-bold' | 'centered' | 'split' | 'stacked';
};

const MOCK_TEMPLATES: TemplateTheme[] = [
  { id: 'modern-minimal', name: 'Modern Minimal', category: 'Professional',  primary: '#1f2937', accent: '#6b7280', bg: '#f8fafc', heading: 'font-sans',    layout: 'centered'  },
  { id: 'warm-artisan',   name: 'Warm Artisan',   category: 'Local service', primary: '#b45309', accent: '#fbbf24', bg: '#1c1410', heading: 'font-display', layout: 'hero-bold' },
  { id: 'bold-impact',    name: 'Bold Impact',    category: 'High energy',   primary: '#dc2626', accent: '#f87171', bg: '#0a0a0a', heading: 'font-sans',    layout: 'hero-bold' },
  { id: 'tech-startup',   name: 'Tech Startup',   category: 'Modern',        primary: '#4f46e5', accent: '#a78bfa', bg: '#0f172a', heading: 'font-sans',    layout: 'split'     },
  { id: 'royal-crest',    name: 'Royal Crest',    category: 'Premium',       primary: '#854d0e', accent: '#facc15', bg: '#1a1611', heading: 'font-display', layout: 'centered'  },
  { id: 'paper-fold',     name: 'Paper Fold',     category: 'Editorial',     primary: '#44403c', accent: '#78716c', bg: '#fafaf9', heading: 'font-display', layout: 'stacked'   },
];

function MiniSitePreview({ t, business }: { t: TemplateTheme; business: string }) {
  const isDark = t.bg.startsWith('#0') || t.bg.startsWith('#1');
  const textMain = isDark ? '#ffffff' : '#111827';
  const textSub = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(17,24,39,0.55)';
  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(17,24,39,0.04)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(17,24,39,0.08)';

  const Header = () => (
    <div
      className="flex items-center justify-between px-2.5 py-1.5 border-b"
      style={{ borderColor: cardBorder }}
    >
      <div className="flex items-center gap-1">
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: t.primary }} />
        <span className="text-[6px] font-bold uppercase tracking-wider" style={{ color: textMain }}>
          {business.split(' ')[0].toLowerCase()}
        </span>
      </div>
      <div className="flex gap-1.5">
        {['Home', 'About', 'Contact'].map((l) => (
          <span key={l} className="text-[5px]" style={{ color: textSub }}>{l}</span>
        ))}
      </div>
    </div>
  );

  const Hero = () => {
    if (t.layout === 'split') {
      return (
        <div className="flex gap-2 px-2.5 py-3">
          <div className="flex-1">
            <div className="h-1 rounded mb-1 w-2/3" style={{ background: textMain }} />
            <div className="h-[3px] rounded mb-[3px]" style={{ background: textSub, opacity: 0.5 }} />
            <div className="h-[3px] rounded mb-1.5 w-4/5" style={{ background: textSub, opacity: 0.5 }} />
            <div className="h-[6px] w-8 rounded" style={{ background: t.primary }} />
          </div>
          <div className="w-[38%] h-[38px] rounded" style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})` }} />
        </div>
      );
    }
    return (
      <div className={`px-2.5 py-3 ${t.layout === 'centered' ? 'text-center' : ''}`} style={{ background: t.layout === 'hero-bold' ? `linear-gradient(135deg, ${t.primary}25, transparent)` : undefined }}>
        <div className={`h-1 rounded mb-1 ${t.layout === 'centered' ? 'mx-auto w-1/2' : 'w-3/4'}`} style={{ background: textMain }} />
        <div className="h-[3px] rounded mb-[3px]" style={{ background: textSub, opacity: 0.5 }} />
        <div className={`h-[3px] rounded mb-1.5 ${t.layout === 'centered' ? 'mx-auto w-5/6' : 'w-4/5'}`} style={{ background: textSub, opacity: 0.5 }} />
        <div className={`flex gap-1 ${t.layout === 'centered' ? 'justify-center' : ''}`}>
          <div className="h-[6px] w-8 rounded" style={{ background: t.primary }} />
          <div className="h-[6px] w-6 rounded border" style={{ borderColor: t.primary }} />
        </div>
      </div>
    );
  };

  const Services = () => (
    <div className="px-2.5 pb-2">
      <div className="grid grid-cols-3 gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded p-1" style={{ background: cardBg, border: `0.5px solid ${cardBorder}` }}>
            <div className="h-[5px] w-[5px] rounded mb-0.5" style={{ background: t.accent }} />
            <div className="h-[2px] rounded mb-[1px]" style={{ background: textMain, opacity: 0.8 }} />
            <div className="h-[2px] rounded w-3/4" style={{ background: textSub, opacity: 0.4 }} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`w-full h-full rounded overflow-hidden ${t.heading}`} style={{ background: t.bg }}>
      <Header />
      <Hero />
      <Services />
    </div>
  );
}

function TemplateStage({ niche, business, city, onNext }: { niche: string; business: string; city: string; onNext: () => void }) {
  void niche; void city;
  const suggestedId = 'warm-artisan';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 5 of 10" />

      <Coach position="above">
        Romulus picked <span className="text-primary-300 font-semibold">Warm Artisan</span> for {business}. Tap it to keep going.
      </Coach>

      <AppWindow title="romulus · pick a template">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-3">
          50 templates · showing top 6
        </p>
        <div className="grid grid-cols-3 gap-3">
          {MOCK_TEMPLATES.map((t) => {
            const isSuggested = t.id === suggestedId;
            return (
              <motion.button
                key={t.id}
                onClick={() => isSuggested && onNext()}
                animate={isSuggested ? { scale: [1, 1.015, 1] } : { scale: 1 }}
                transition={isSuggested ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
                className={`relative text-left rounded-lg overflow-hidden border transition-all ${
                  isSuggested
                    ? 'border-primary-400/60 cursor-pointer'
                    : 'border-white/10 opacity-55 pointer-events-none'
                }`}
                style={isSuggested ? { boxShadow: '0 0 30px rgba(176,128,128,0.45)' } : undefined}
              >
                {isSuggested && (
                  <span className="absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded bg-primary-500 text-[7px] font-bold tracking-[0.15em] uppercase text-white">
                    Suggested
                  </span>
                )}
                {/* Mini site preview */}
                <div className="h-[110px] overflow-hidden">
                  <MiniSitePreview t={t} business={business} />
                </div>
                <div className="px-2 py-1.5 bg-black/50 flex items-center justify-between border-t border-white/5">
                  <div>
                    <p className="text-[11px] font-bold text-white leading-tight">{t.name}</p>
                    <p className="text-[9px] text-gray-500">{t.category}</p>
                  </div>
                  {isSuggested && <ArrowRight className="w-3 h-3 text-primary-300" />}
                </div>
                {isSuggested && (
                  <div className="absolute left-[55%] top-[35%] z-30">
                    <TutorialCursor />
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </AppWindow>
    </motion.div>
  );
}

// ─── CustomizeStage (new: pick a color scheme, watch preview swap) ───────
// Mini editor view. Palette swatches on the left, a live preview card on
// the right that repaints as the user picks a color. "Generate" is gated
// only until a color's been acknowledged - tapping any swatch (even the
// default) unlocks the button. Keeps the step snappy.
// ─── CustomizeStage (full site editor · 4 tabs + live preview) ───────────
// Four tabs match the real editor's control groups: content (headline +
// tagline + CTA), colors (palette swatches), fonts (Sans / Serif / Mono),
// layout (section toggles). Everything the user types or toggles re-paints
// the preview card on the right in real time.
const EDITOR_PALETTES: { name: string; primary: string; accent: string; bg: string }[] = [
  { name: 'Warm Amber',   primary: '#d97706', accent: '#fbbf24', bg: '#1f1410' },
  { name: 'Rose Bronze',  primary: '#b45309', accent: '#f59e0b', bg: '#1a1412' },
  { name: 'Slate Ink',    primary: '#475569', accent: '#94a3b8', bg: '#0f172a' },
  { name: 'Coastal Teal', primary: '#0d9488', accent: '#5eead4', bg: '#0f1e1e' },
];

const EDITOR_FONTS: { label: string; className: string; stack: string }[] = [
  { label: 'Sans',    className: 'font-sans',    stack: 'Lexend, system-ui, sans-serif' },
  { label: 'Serif',   className: 'font-display', stack: 'Lora, Georgia, serif' },
  { label: 'Roman',   className: 'font-roman',   stack: 'Cinzel, serif' },
  { label: 'Mono',    className: 'font-mono',    stack: 'Fira Code, monospace' },
];

type EditorSections = {
  hero: boolean;
  services: boolean;
  testimonials: boolean;
  gallery: boolean;
  contact: boolean;
};

function CustomizeStage({ niche, business, city, onNext }: {
  niche: string; business: string; city: string; onNext: () => void;
}) {
  const nicheLower = niche.toLowerCase().replace(/\bhvac\b/g, 'HVAC').replace(/s$/, '');
  const [tab, setTab] = useState<'content' | 'colors' | 'fonts' | 'layout'>('content');
  const [headline, setHeadline] = useState(`${business}`);
  const [tagline, setTagline] = useState(`Professional ${nicheLower} services in ${city}`);
  const [cta, setCta] = useState('Get a free quote');
  const [colorIdx, setColorIdx] = useState(0);
  const [fontIdx, setFontIdx] = useState(1);
  const [sections, setSections] = useState<EditorSections>({
    hero: true, services: true, testimonials: true, gallery: false, contact: true,
  });
  const [touched, setTouched] = useState(true);

  const color = EDITOR_PALETTES[colorIdx];
  const font = EDITOR_FONTS[fontIdx];

  const mark = () => { if (!touched) setTouched(true); };

  const TabButton = ({ id, label }: { id: typeof tab; label: string }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${
        tab === id
          ? 'bg-primary-500/15 border border-primary-500/40 text-primary-200'
          : 'bg-white/[0.02] border border-transparent text-gray-400 hover:text-white hover:bg-white/[0.04]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 8 of 10" />

      <Coach position="above">
        Edit your generated site live. Change text, pick colors, swap fonts, toggle sections. Hit <span className="text-primary-300 font-semibold">Save</span> when it feels right.
      </Coach>

      <AppWindow title="romulus · editor">
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
          {/* Controls column */}
          <div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              <TabButton id="content" label="Content" />
              <TabButton id="colors" label="Colors" />
              <TabButton id="fonts" label="Fonts" />
              <TabButton id="layout" label="Layout" />
            </div>

            <div className="min-h-[260px]">
              {tab === 'content' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Headline</label>
                    <input
                      value={headline}
                      onChange={(e) => { setHeadline(e.target.value); mark(); }}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-1.5 text-[12px] text-white outline-none focus:border-primary-400/50"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Tagline</label>
                    <input
                      value={tagline}
                      onChange={(e) => { setTagline(e.target.value); mark(); }}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-1.5 text-[12px] text-white outline-none focus:border-primary-400/50"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">CTA button</label>
                    <input
                      value={cta}
                      onChange={(e) => { setCta(e.target.value); mark(); }}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-1.5 text-[12px] text-white outline-none focus:border-primary-400/50"
                    />
                  </div>
                </div>
              )}

              {tab === 'colors' && (
                <div className="space-y-2">
                  {EDITOR_PALETTES.map((p, i) => (
                    <button
                      key={p.name}
                      onClick={() => { setColorIdx(i); mark(); haptic(6); }}
                      className={`w-full flex items-center gap-2.5 p-2 rounded-lg border transition-all ${
                        i === colorIdx
                          ? 'border-primary-400 bg-primary-500/[0.08]'
                          : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                      }`}
                    >
                      <div className="flex gap-0.5 flex-shrink-0">
                        <div className="w-4 h-4 rounded" style={{ background: p.primary }} />
                        <div className="w-4 h-4 rounded" style={{ background: p.accent }} />
                      </div>
                      <span className={`text-[11px] font-medium ${i === colorIdx ? 'text-white' : 'text-gray-400'}`}>{p.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {tab === 'fonts' && (
                <div className="space-y-2">
                  {EDITOR_FONTS.map((f, i) => (
                    <button
                      key={f.label}
                      onClick={() => { setFontIdx(i); mark(); haptic(6); }}
                      className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-lg border transition-all text-left ${
                        i === fontIdx
                          ? 'border-primary-400 bg-primary-500/[0.08]'
                          : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                      }`}
                    >
                      <div>
                        <p className={`text-[12px] font-bold ${i === fontIdx ? 'text-white' : 'text-gray-300'}`} style={{ fontFamily: f.stack }}>
                          Aa {business.split(' ')[0]}
                        </p>
                        <p className="text-[9px] text-gray-500 uppercase tracking-wider mt-0.5">{f.label}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {tab === 'layout' && (
                <div className="space-y-2">
                  {(Object.entries(sections) as [keyof EditorSections, boolean][]).map(([k, v]) => (
                    <label key={k} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-white/10 bg-white/[0.02] cursor-pointer hover:border-white/20">
                      <span className={`text-[11px] font-medium capitalize ${v ? 'text-white' : 'text-gray-500'}`}>{k}</span>
                      <button
                        onClick={(e) => { e.preventDefault(); setSections({ ...sections, [k]: !v }); mark(); haptic(6); }}
                        className={`relative w-8 h-[18px] rounded-full transition-colors ${v ? 'bg-primary-500' : 'bg-white/10'}`}
                      >
                        <span
                          className="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all"
                          style={{ left: v ? 'calc(100% - 16px)' : '2px' }}
                        />
                      </button>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Live preview column - repaints on every change */}
          <motion.div
            key={`${colorIdx}-${fontIdx}`}
            initial={{ opacity: 0.7, scale: 0.99 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="rounded-lg overflow-hidden border border-white/10"
            style={{ background: color.bg, fontFamily: font.stack }}
          >
            {/* Hero */}
            {sections.hero && (
              <div
                className="px-4 py-5"
                style={{ background: `linear-gradient(135deg, ${color.primary}33, transparent)` }}
              >
                <div className="flex items-center gap-1.5 mb-3">
                  <div className="w-2 h-2 rounded-full" style={{ background: color.primary }} />
                  <span className="text-[8px] font-bold uppercase tracking-widest text-white/60">{business.split(' ')[0]}</span>
                </div>
                <p className="text-[16px] font-bold text-white leading-tight mb-1">{headline || business}</p>
                <p className="text-[10px] text-white/60 mb-3">{tagline}</p>
                <div className="flex gap-1.5">
                  <span
                    className="inline-block px-2.5 py-1 rounded text-[9px] font-bold text-white"
                    style={{ background: color.primary }}
                  >
                    {cta || 'Contact us'}
                  </span>
                  <span
                    className="inline-block px-2.5 py-1 rounded text-[9px] font-bold border"
                    style={{ borderColor: color.accent, color: color.accent }}
                  >
                    Learn more
                  </span>
                </div>
              </div>
            )}

            {/* Services */}
            {sections.services && (
              <div className="px-4 py-3 border-t border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-white/50 mb-2">Services</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="rounded p-1.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <div className="w-1.5 h-1.5 rounded mb-1" style={{ background: color.accent }} />
                      <div className="h-[2px] rounded mb-0.5" style={{ background: 'rgba(255,255,255,0.7)' }} />
                      <div className="h-[2px] rounded w-3/4" style={{ background: 'rgba(255,255,255,0.35)' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Testimonials */}
            {sections.testimonials && (
              <div className="px-4 py-3 border-t border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-white/50 mb-2">Reviews</p>
                <div className="flex gap-1.5">
                  {[4.8, 4.9].map((r) => (
                    <div key={r} className="flex-1 rounded p-1.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <div className="h-[2px] rounded mb-0.5" style={{ background: 'rgba(255,255,255,0.6)' }} />
                      <div className="h-[2px] rounded w-4/5" style={{ background: 'rgba(255,255,255,0.3)' }} />
                      <p className="text-[8px] mt-1" style={{ color: color.accent }}>★ {r}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gallery */}
            {sections.gallery && (
              <div className="px-4 py-3 border-t border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-white/50 mb-2">Gallery</p>
                <div className="grid grid-cols-4 gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="aspect-square rounded" style={{ background: `linear-gradient(135deg, ${color.primary}66, ${color.accent}44)` }} />
                  ))}
                </div>
              </div>
            )}

            {/* Contact */}
            {sections.contact && (
              <div className="px-4 py-3 border-t border-white/5">
                <p className="text-[8px] font-bold uppercase tracking-widest text-white/50 mb-1.5">Contact</p>
                <p className="text-[9px] text-white/70 mb-1">{city}</p>
                <div className="h-[18px] rounded w-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
              </div>
            )}
          </motion.div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-[10px] text-gray-500">Everything here also works in the real editor.</p>
          <motion.div
            animate={touched ? { scale: [1, 1.03, 1] } : { scale: 1 }}
            transition={touched ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
            className="relative w-full sm:w-auto"
          >
            <button
              onClick={() => touched && onNext()}
              disabled={!touched}
              className={`w-full sm:w-auto px-5 py-2.5 rounded-lg font-semibold text-[13px] flex items-center justify-center gap-2 whitespace-nowrap transition-all ${
                touched
                  ? 'bg-gradient-to-r from-primary-500 to-primary-400 text-white'
                  : 'bg-white/[0.04] text-gray-600 cursor-not-allowed'
              }`}
              style={touched ? { boxShadow: '0 0 30px rgba(176,128,128,0.6)' } : undefined}
            >
              Save &amp; continue
              <ArrowRight className="w-4 h-4" />
            </button>
            {touched && <TutorialCursor />}
          </motion.div>
        </div>
      </AppWindow>
    </motion.div>
  );
}
// ─── FeaturesStage (pick which modules the site ships with) ─────────────
// Sits between Template and Customize. Shows 8 feature modules as toggle
// cards. A few are on by default. User toggles any to feel the
// configurability, then hits Continue. The picked set is threaded through
// to downstream stages so the preview reflects their choice.
const FEATURE_CARDS: { id: string; label: string; desc: string; icon: React.ComponentType<{ className?: string }>; defaultOn: boolean }[] = [
  { id: 'booking',       label: 'Online booking',   desc: 'Let visitors book direct.',       icon: Calendar,       defaultOn: false },
  { id: 'pricing',       label: 'Pricing table',    desc: 'Show packages upfront.',          icon: DollarSign,     defaultOn: false },
  { id: 'gallery',       label: 'Photo gallery',    desc: 'Showcase past work.',             icon: ImageIcon,      defaultOn: true  },
  { id: 'testimonials',  label: 'Reviews',          desc: 'Build trust with quotes.',        icon: MessageSquare,  defaultOn: true  },
  { id: 'faq',           label: 'FAQ',              desc: 'Head off common questions.',      icon: HelpCircle,     defaultOn: false },
  { id: 'contact',       label: 'Contact form',     desc: 'Capture leads 24/7.',             icon: Mail,           defaultOn: true  },
  { id: 'map',           label: 'Service map',      desc: 'Show your coverage area.',        icon: Map,            defaultOn: false },
  { id: 'callnow',       label: 'Click-to-call',    desc: 'One-tap phone button.',           icon: Phone,          defaultOn: true  },
];

function FeaturesStage({ business, onNext }: {
  business: string;
  onNext: (features: string[]) => void;
}) {
  const [enabled, setEnabled] = useState<Record<string, boolean>>(
    () => Object.fromEntries(FEATURE_CARDS.map((c) => [c.id, c.defaultOn]))
  );
  const [touched, setTouched] = useState(true);
  const toggle = (id: string) => {
    setTouched(true);
    setEnabled((s) => ({ ...s, [id]: !s[id] }));
    haptic(6);
  };
  const count = Object.values(enabled).filter(Boolean).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 6 of 10" />

      <Coach position="above">
        Pick the modules {business}'s site needs. Toggle any on or off.
      </Coach>

      <AppWindow title="romulus · features">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-3">
          Features · {count} of {FEATURE_CARDS.length} enabled
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {FEATURE_CARDS.map((f) => {
            const on = enabled[f.id];
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => toggle(f.id)}
                className={`text-left rounded-xl border p-3 transition-all ${
                  on
                    ? 'border-primary-400/60 bg-primary-500/[0.08]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                }`}
                style={on ? { boxShadow: '0 0 20px rgba(176,128,128,0.2)' } : undefined}
              >
                <div className="flex items-start justify-between mb-2">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      on ? 'bg-primary-500/20 text-primary-300' : 'bg-white/[0.04] text-gray-500'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <span
                    className={`relative w-7 h-4 rounded-full transition-colors ${on ? 'bg-primary-500' : 'bg-white/10'}`}
                  >
                    <span
                      className="absolute top-[2px] w-3 h-3 rounded-full bg-white transition-all"
                      style={{ left: on ? 'calc(100% - 14px)' : '2px' }}
                    />
                  </span>
                </div>
                <p className={`text-[12px] font-semibold leading-tight ${on ? 'text-white' : 'text-gray-400'}`}>
                  {f.label}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5 leading-snug">{f.desc}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-[10px] text-gray-500">Swap them anytime in the real editor.</p>
          <motion.div
            animate={touched ? { scale: [1, 1.03, 1] } : { scale: 1 }}
            transition={touched ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
            className="relative w-full sm:w-auto"
          >
            <button
              onClick={() => touched && onNext(FEATURE_CARDS.filter((c) => enabled[c.id]).map((c) => c.id))}
              disabled={!touched}
              className={`w-full sm:w-auto px-5 py-2.5 rounded-lg font-semibold text-[13px] flex items-center justify-center gap-2 whitespace-nowrap transition-all ${
                touched
                  ? 'bg-gradient-to-r from-primary-500 to-primary-400 text-white'
                  : 'bg-white/[0.04] text-gray-600 cursor-not-allowed'
              }`}
              style={touched ? { boxShadow: '0 0 30px rgba(176,128,128,0.6)' } : undefined}
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
            {touched && <TutorialCursor />}
          </motion.div>
        </div>
      </AppWindow>
    </motion.div>
  );
}

// ─── LeadsStage ───────────────────────────────────────────────────────────
// Mock leads list with one row spotlighted. User clicks "Generate site" on
// the highlighted lead to advance.

function LeadsStage({ niche, city, leads, onNext }: { niche: string; city: string; leads: number; onNext: () => void }) {
  const nicheLeads = (NICHE_MOCK_LEADS[niche] ?? NICHE_MOCK_LEADS.Plumbers).slice(0, Math.min(leads, 4));
  const displayedLeads = nicheLeads.map((l) => ({ name: l.name, city, rating: l.rating }));
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 4 of 10" />

      <Coach position="above">
        Pick a lead and let Romulus build their site. Try the top one.
      </Coach>

      <AppWindow title={`romulus · leads (${leads})`}>
        <div className="space-y-2 relative">
          {/* Spotlighted lead - rendered first in natural document order.
              Everything below gets the dim+blur overlay. */}
          <motion.div
            animate={{ scale: [1, 1.015, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            className="relative z-30 flex items-center justify-between rounded-lg border border-primary-400/60 bg-primary-500/[0.1] px-3 py-2.5"
            style={{ boxShadow: '0 0 40px rgba(176,128,128,0.5)' }}
          >
            <div>
              <p className="text-[13px] text-white font-semibold">{displayedLeads[0]?.name}</p>
              <p className="text-[10px] text-gray-400">{displayedLeads[0]?.city} · ★ {displayedLeads[0]?.rating}</p>
            </div>
            <div className="relative">
              <button
                onClick={onNext}
                className="text-[11px] text-white font-semibold bg-gradient-to-r from-primary-500 to-primary-400 rounded-md px-3 py-1.5 flex items-center gap-1.5"
                style={{ boxShadow: '0 0 20px rgba(176,128,128,0.6)' }}
              >
                Generate site
                <ArrowRight className="w-3 h-3" />
              </button>
              <TutorialCursor />
            </div>
          </motion.div>

          {/* Remaining leads dimmed + non-interactive. Wrapper applies the
              dim overlay so no negative-margin hacks are needed. */}
          <div className="relative">
            <div className="relative space-y-2 opacity-40 pointer-events-none">
              {displayedLeads.slice(1).map((lead) => (
                <div key={lead.name} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                  <div>
                    <p className="text-[13px] text-white font-medium">{lead.name}</p>
                    <p className="text-[10px] text-gray-500">{lead.city} · ★ {lead.rating}</p>
                  </div>
                  <button className="text-[11px] text-gray-500 border border-white/10 rounded-md px-2.5 py-1.5">
                    Generate site
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AppWindow>
    </motion.div>
  );
}

// ─── OutreachStage ────────────────────────────────────────────────────────
// ─── SiteGenerationStage (inline HackerLoadingScreen · generating mode) ──
// After customize, before the sites page. Uses the exact same CLI as the
// real product's site-gen loading screen, but embedded inside a mini
// desktop window rather than full-screen.
function SiteGenerationStage({ business, features, onNext }: { business: string; features: string[]; onNext: () => void }) {
  const [logs, setLogs] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const TOTAL_MS = 3800;

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const featureLine = features.length
      ? `Wiring ${features.length} feature module${features.length === 1 ? '' : 's'}`
      : 'Composing services section';
    const LINES = [
      `Loading template data for ${business}`,
      'Generating hero copy with Gemini',
      featureLine,
      'Rendering final HTML + assets',
    ];
    LINES.forEach((line, i) => {
      timers.push(setTimeout(() => setLogs((prev) => [...prev, line]), 300 + i * 650));
    });
    timers.push(setTimeout(() => setDone(true), TOTAL_MS));

    const started = performance.now();
    let raf = 0;
    const tick = () => {
      const t = Math.min(1, (performance.now() - started) / TOTAL_MS);
      setProgress(Math.round(t * 100));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(raf);
    };
  }, [business, features.length]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[60vh]"
    >
      <PracticeBadge stepOf="Step 7 of 10" />
      <Coach position="above">
        Romulus is compiling <span className="text-primary-300 font-semibold">{business}</span>'s site.
      </Coach>
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-black/80 overflow-hidden" style={{ boxShadow: '0 20px 80px rgba(0,0,0,0.6)' }}>
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-white/[0.03]">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          <span className="text-[10px] text-gray-500 ml-3 font-mono tracking-wide">romulus · generating</span>
        </div>
        <div className="relative" style={{ height: 440 }}>
          <HackerLoadingScreen
            isOpen
            inline
            title="GENERATING SITE"
            subtitle={business}
            mode="generating"
            logs={logs}
            stats={{ progress }}
          />
        </div>
      </div>

      {done && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md mt-6"
        >
          <Button size="lg" onClick={onNext} className="w-full h-14 text-base group">
            <span className="flex items-center justify-center gap-2">
              Site ready - open it
              <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
            </span>
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

function OutreachStage({ business, onNext }: { business: string; onNext: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
    >
      <PracticeBadge stepOf="Step 10 of 10" />

      <Coach position="above">
        Your site is ready. Fire off the pitch.
      </Coach>

      <AppWindow title="romulus · send pitch">
        <DimBackdrop />
        <div className="relative z-20 opacity-55 pointer-events-none mb-5">
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-3">
            To: {business}
          </p>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5">
            <p className="text-[11px] text-gray-500 mb-1">Subject</p>
            <p className="text-[13px] text-white mb-3 font-medium">A live website for {business} - take a look</p>
            <p className="text-[11px] text-gray-500 mb-1">Body</p>
            <p className="text-[12px] text-gray-300 leading-snug">
              Hi - I built a free preview of a website for your business…
            </p>
          </div>
        </div>

        <div className="relative z-30 flex justify-end">
          <motion.div
            animate={{ scale: hovered ? 1.04 : [1, 1.03, 1] }}
            transition={hovered ? { duration: 0.2 } : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            className="relative"
          >
            <button
              onClick={onNext}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              className="relative px-5 py-2.5 rounded-lg bg-gradient-to-r from-primary-500 to-primary-400 text-white font-semibold text-[13px] flex items-center gap-2"
              style={{ boxShadow: '0 0 40px rgba(176,128,128,0.7), 0 0 0 2px rgba(176,128,128,0.3)' }}
            >
              Send pitch
              <ArrowRight className="w-4 h-4" />
            </button>
            <TutorialCursor />
          </motion.div>
        </div>
      </AppWindow>
    </motion.div>
  );
}

// ─── RevealStage ──────────────────────────────────────────────────────────
// Big timer reveal after the 4-step walkthrough. Peak-end beat - rings
// expand behind the number, typography is massive.
// Stopwatch-style reveal. The clock visibly counts up from 00.0 and locks
// on the user's actual ms, then the rest of the screen settles in around
// it. Matches the "timer" mental model the user asked for instead of the
// older burst-in animation.
function RevealStage({ ms, count, onNext, ctaLabel = 'Show me how I get paid' }: {
  ms: number;
  count: number;
  onNext: () => void;
  // Desktop hands off to the Stripe Connect payout explainer, so its button
  // promises that. Mobile has no payout stage and goes straight to the numbers,
  // so it must not promise a screen that never arrives.
  ctaLabel?: string;
}) {
  const [displayed, setDisplayed] = useState(0);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    const COUNT_DURATION = 1800;
    const started = performance.now();
    let raf = 0;
    const tick = () => {
      const t = Math.min(1, (performance.now() - started) / COUNT_DURATION);
      const eased = 1 - Math.pow(1 - t, 2.5);
      setDisplayed(ms * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setLocked(true);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ms]);

  const fmt = formatStopwatch(displayed);
  const totalSecs = ms / 1000;
  const finalMins = Math.floor(totalSecs / 60);
  const finalSecs = Math.round(totalSecs - finalMins * 60);
  const cycleLabel = finalMins > 0 ? `${finalMins}m ${finalSecs}s` : `${finalSecs}s`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="flex-1 flex flex-col items-center justify-center pt-2 w-full min-h-[70vh] relative"
    >
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="text-primary-400 text-[11px] font-semibold tracking-[0.25em] uppercase mb-6"
      >
        Stopwatch
      </motion.p>

      {/* Stopwatch frame: thin rounded pill, digital-mono digits, blinking
          colon. Subtle primary glow while ticking; green-ish lock pulse
          when it stops. */}
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="relative rounded-2xl border border-white/10 bg-black/60 px-5 sm:px-10 md:px-14 py-5 sm:py-6 md:py-8 mb-10 max-w-[calc(100vw-2rem)]"
        style={{ boxShadow: locked ? '0 0 80px rgba(176,128,128,0.5)' : '0 0 50px rgba(176,128,128,0.25)' }}
      >
        {/* Tick indicator in corner */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <motion.span
            animate={{ opacity: locked ? 1 : [0.4, 1, 0.4] }}
            transition={{ duration: 0.8, repeat: locked ? 0 : Infinity, ease: 'easeInOut' }}
            className={`w-1.5 h-1.5 rounded-full ${locked ? 'bg-primary-300' : 'bg-primary-400'}`}
          />
          <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-gray-500">
            {locked ? 'Time · Locked' : 'Time'}
          </span>
        </div>

        <div className="flex items-baseline font-mono tabular-nums">
          <span
            className="text-[36px] sm:text-[56px] md:text-[88px] font-bold text-white leading-none whitespace-nowrap"
            style={{ textShadow: locked ? '0 0 60px rgba(176,128,128,0.8)' : 'none' }}
          >
            {fmt}
          </span>
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: locked ? 1 : 0, y: locked ? 0 : 6 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="text-center text-[17px] md:text-[19px] text-gray-300 leading-snug max-w-lg mb-3 px-4"
      >
        You just ran one full pitch cycle in <span className="text-white font-semibold tabular-nums">{cycleLabel}</span>.
      </motion.p>
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: locked ? 1 : 0, y: locked ? 0 : 6 }}
        transition={{ duration: 0.4, delay: 0.25 }}
        className="text-center text-[14px] text-gray-400 leading-relaxed mb-8 max-w-md px-4"
      >
        Now repeat it for each of the <span className="text-white font-semibold">{count}</span> leads you scraped.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: locked ? 1 : 0, y: locked ? 0 : 8 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="w-full max-w-md"
      >
        <Button size="lg" onClick={onNext} className="w-full h-14 text-base group">
          <span className="flex items-center justify-center gap-2">
            {ctaLabel}
            <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
          </span>
        </Button>
      </motion.div>
    </motion.div>
  );
}

// ─── RaceStage ────────────────────────────────────────────────────────────
// Side-by-side timeline race comparing Romulus to a traditional workflow.
// The timer on the left accelerates through 6 stages in ~8 real seconds;
// the timer on the right trickles through a couple of stages over the same
// window, visualising how a conventional agency workflow lags.
// Shared stages for the 3-lane race. The race is framed along the six
// end-to-end moves needed to actually deliver a revenue-producing site.
// Every lane gets scored against these same milestones so the user can
// see exactly where each method breaks down.


function FunnelPlanPicker({
  answers,
  closesPerMonth,
  pricePerSite,
  simulationMs,
  loading,
  onCheckout,
  isMobile,
}: {
  answers: FunnelAnswers;
  closesPerMonth: number;
  pricePerSite: number;
  simulationMs: number;
  loading: boolean;
  onCheckout: (planId: PlanId) => void;
  isMobile: boolean;
}) {
  const pickerPlans = isMobile ? MOBILE_PICKER_PLANS : FUNNEL_PICKER_PLANS;
  // On desktop the recommendation follows the hours answer. On mobile Lifetime is
  // pre-selected regardless — it is the offer the screen is built around.
  const defaultPlan: PlanId = answers.hours ? RECOMMENDED_PLAN[answers.hours] : 'starter';
  const [selected, setSelected] = useState<PlanId>(isMobile ? 'lifetime' : defaultPlan);

  const fmt = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
  const secs = (simulationMs / 1000).toFixed(1);

  const valueForPlan = (planId: PlanId) => {
    const quota = PLAN_CONFIGS[planId].quotas.sitesLimit;
    const closes = Math.min(closesPerMonth, quota);
    const gross = closes * pricePerSite;
    const planPrice = PLAN_CONFIGS[planId].price;
    const oneTime = isOneTimePlan(planId);
    // ROI is measured over ONE YEAR, not one month, and this is the only way to
    // compare a one-time plan with a subscription honestly.
    //
    // The old formula was gross / planPrice, i.e. a single month against a single
    // charge. On a subscription that is fine (the monthly ratio and the yearly
    // ratio are the same number - gross*12 / price*12 reduces to gross / price,
    // so every desktop figure is byte-identical to what it was). On Lifetime it
    // was actively backwards: $2,500 / $79 = 32x scored WORSE than Starter's
    // $2,500 / $49 = 51x, because it charged Lifetime a full year of value
    // against a payment that never repeats. Over twelve months Lifetime is
    // $30,000 / $79 = 380x against Starter's $30,000 / $588 = 51x.
    const yearGross = gross * 12;
    const yearCost = oneTime ? planPrice : planPrice * 12;
    const roi = yearCost > 0 ? yearGross / yearCost : 0;
    // A one-time plan bills nothing monthly, so nothing is deducted from the
    // monthly take-home. Subtracting $79 every month here would understate
    // Lifetime against a subscription and be plainly wrong.
    const netMonthly = oneTime ? gross : gross - planPrice;
    return { quota, closes, gross, planPrice, roi, netMonthly, yearGross, yearCost, oneTime };
  };

  const selectedValue = valueForPlan(selected);
  const selectedIsOneTime = isOneTimePlan(selected);
  const animatedRoi = useCountUp(Math.round(selectedValue.roi), 1400, selected);
  // How many times better Lifetime's year-one ROI is than the plan sitting next
  // to it. Mobile shows exactly two cards, so "the other one" is unambiguous.
  // Floored, never rounded up, and dropped entirely below 2x - the badge is only
  // allowed to understate. Recomputes from PLAN_CONFIGS, so a reprice moves it.
  const roiEdge = (() => {
    if (!isMobile) return null;
    const rival = pickerPlans.find((p) => !isOneTimePlan(p));
    if (!rival) return null;
    const lifetimeRoi = valueForPlan('lifetime').roi;
    const rivalRoi = valueForPlan(rival).roi;
    if (!lifetimeRoi || !rivalRoi) return null;
    const edge = Math.floor(lifetimeRoi / rivalRoi);
    return edge >= 2 ? edge : null;
  })();
  const identity = answers.hours ? IDENTITY[answers.hours] : null;
  const dailyMissed = Math.round(selectedValue.netMonthly / 30);
  const launchActive = isLaunchWeekActive();
  const nicheLabel = answers.niche
    ? (NICHE_OPTIONS.find((o) => o.id === answers.niche)?.label ?? 'your niche')
    : 'your niche';

  // Mint the hosted Checkout Session while the user is still reading, not after
  // they tap. Mobile's whole checkout is one redirect, so the only latency left
  // in it was this callable — about a second on mobile data, spent staring at a
  // spinner. Fired for whichever plan is currently selected, including the
  // preselected one on mount, so switching cards pre-warms the new choice too.
  //
  // Safe to run on every selection: a hosted Session creates no Customer and no
  // PaymentIntent until the buyer submits, so an unused one simply expires. This
  // is NOT the desktop prefetch, which mints a real incomplete Subscription.
  useEffect(() => {
    if (!isMobile) return;
    prefetchHostedCheckout(selected);
  }, [isMobile, selected]);

  // The picker is broken into 3 sub-slides with AnimatePresence transitions
  // between them so each beat gets its own moment instead of being a dense
  // scroll. Progression is always forward (commitment ratchet).
  const [subSlide, setSubSlide] = useState<0 | 1 | 2>(0);

  const slideVariants = {
    enter:  { x: 40, opacity: 0 },
    center: { x: 0,  opacity: 1 },
    exit:   { x: -40, opacity: 0 },
  };

  return (
    <div className="flex-1 flex flex-col items-center pt-2 w-full">
      <div className="flex items-center justify-center gap-1.5 mb-5">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            animate={{
              width: i === subSlide ? 22 : 6,
              opacity: i <= subSlide ? 1 : 0.3,
            }}
            transition={{ duration: 0.35 }}
            className={`h-1.5 rounded-full ${i <= subSlide ? 'bg-primary-400' : 'bg-white/20'}`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ── Sub-slide 0: Identity + commitment recap ────────────────── */}
        {subSlide === 0 && (
          <motion.div
            key="profile"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full flex-1 flex flex-col items-center justify-center min-h-[60vh]"
          >
            <p className="text-primary-400 text-[10px] font-semibold tracking-[0.25em] uppercase mb-2">
              Your profile
            </p>
            {identity && (
              <>
                <h2 className="text-[28px] md:text-[38px] font-bold text-white leading-tight text-center max-w-xl mb-2">
                  {identity.label}
                </h2>
                <p className="text-[15px] text-gray-400 text-center max-w-md mb-7">{identity.sub}</p>
              </>
            )}

            {/* Commitment ratchet: four ✓ chips mirroring their own answers
                back at them. Staggered entry so each answer lands as its own
                beat. */}
            <div className="flex flex-wrap justify-center gap-2 mb-8 max-w-xl">
              {[
                answers.hours ? HOURS_OPTIONS.find((o) => o.id === answers.hours)?.label : null,
                answers.goal ? GOAL_OPTIONS.find((o) => o.id === answers.goal)?.label : null,
                nicheLabel,
                `$${pricePerSite} / site`,
              ].filter((l): l is string => !!l).map((label, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.9, y: 6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 + i * 0.1 }}
                  className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-primary-500/20 text-[12px] text-gray-200 font-medium"
                >
                  <span className="text-green-400 mr-1">✓</span>{label}
                </motion.span>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              className="w-full max-w-md"
            >
              <Button
                size="lg"
                onClick={() => { haptic(10); setSubSlide(1); }}
                className="w-full h-14 text-base group"
              >
                <span className="flex items-center justify-center gap-2">
                  Show me what it earns
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
              <p className="text-[11px] text-gray-500 text-center mt-3">
                That cycle took you <span className="text-primary-300 font-semibold tabular-nums">{secs}s</span>. Let's do the math.
              </p>
            </motion.div>
          </motion.div>
        )}

        {/* ── Sub-slide 1: The ROI hero (peak-end beat) ────────────────── */}
        {subSlide === 1 && (
          <motion.div
            key="roi"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
          >
            <p className="text-primary-400 text-[10px] font-semibold tracking-[0.25em] uppercase mb-2">
              Your hypothetical return
            </p>
            <h2 className="text-[26px] md:text-[32px] font-bold text-white leading-tight text-center max-w-xl mb-6">
              We built your plan.<br />Now let's run it.
            </h2>

            <motion.div
              key={selected}
              initial={{ opacity: 0.6, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="w-full max-w-xl rounded-2xl border border-primary-500/40 p-6 md:p-7 mb-6 relative overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, rgba(91,58,58,0.14), rgba(91,58,58,0.03))',
                boxShadow: '0 0 70px rgba(91,58,58,0.35)',
              }}
            >
              {/* Halo ring behind the ratio for a subtle peak-end beat. */}
              <motion.div
                initial={{ scale: 0.6, opacity: 0.4 }}
                animate={{ scale: 1.3, opacity: 0 }}
                transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] h-[260px] rounded-full border border-primary-400/30 pointer-events-none"
              />

              <div className="flex items-center justify-between mb-5 relative z-10">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.18em] text-gray-500 font-semibold mb-1">You pay</p>
                  <p className="text-2xl md:text-3xl font-bold text-gray-300 tabular-nums">
                    ${selectedValue.planPrice}
                    {/* "once", never "/mo", on a one-time plan. Printing "$79/mo"
                        next to a plan that bills a single time is a misquoted
                        price on the screen that takes the money. */}
                    <span className="text-sm text-gray-500 font-normal">{selectedIsOneTime ? ' once' : '/mo'}</span>
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-primary-300 font-semibold mb-1">You target</p>
                  <p
                    className="text-2xl md:text-3xl font-bold text-white tabular-nums"
                    style={{ textShadow: '0 0 30px rgba(91,58,58,0.7)' }}
                  >
                    {fmt(selectedValue.gross)}
                    <span className="text-sm text-gray-400 font-normal">/mo</span>
                  </p>
                </div>
              </div>

              <div className="text-center py-5 border-y border-white/5 relative z-10">
                <p className="text-[9px] uppercase tracking-[0.18em] text-primary-300 font-semibold mb-1">Return on investment · year one</p>
                <p
                  className="text-[72px] md:text-[96px] font-bold text-white leading-none tabular-nums"
                  style={{ textShadow: '0 0 60px rgba(91,58,58,0.85)' }}
                >
                  {animatedRoi}×
                </p>
                <p className="text-[12px] text-gray-500 mt-3">
                  Your plan pays for itself on the <span className="text-white font-semibold">first sale</span>.
                </p>
              </div>

              <p className="text-[10px] text-gray-600 leading-relaxed text-center mt-4 max-w-md mx-auto relative z-10">
                {/* Two variants, because one sentence cannot describe both billing
                    shapes. The subscription string is the legacy one, unchanged -
                    it is the only one desktop can ever reach. */}
                {selectedIsOneTime
                  ? `Math: ${selectedValue.closes} sites × $${pricePerSite} = ${fmt(selectedValue.gross)} / mo, and the plan is $${selectedValue.planPrice} paid once. Year one: ${fmt(selectedValue.yearGross)} against $${selectedValue.planPrice}.`
                  : `Math: ${selectedValue.closes} sites × $${pricePerSite} = ${fmt(selectedValue.gross)} gross, minus $${selectedValue.planPrice} plan = ${fmt(selectedValue.netMonthly)} net.`}{' '}
                This is <span className="text-white">your hypothetical target</span>, not a guarantee. See our{' '}
                <Link to="/terms" className="text-gray-500 underline underline-offset-2 hover:text-gray-400">earnings disclaimer</Link>.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
              className="w-full max-w-md"
            >
              <Button
                size="lg"
                onClick={() => { haptic(12); setSubSlide(2); }}
                className="w-full h-14 text-base group"
              >
                <span className="flex items-center justify-center gap-2">
                  Pick my plan
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
              {dailyMissed > 5 && (
                <p className="text-[11px] text-gray-500 text-center mt-3">
                  Your projected pace ≈ <span className="text-white font-semibold tabular-nums">{fmt(dailyMissed)}</span> of work per day.
                </p>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* ── Sub-slide 2: Plan selector + activate ────────────────────── */}
        {subSlide === 2 && (
          <motion.div
            key="select"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full flex-1 flex flex-col items-center justify-center min-h-[70vh]"
          >
            <p className="text-primary-400 text-[10px] font-semibold tracking-[0.25em] uppercase mb-2">
              Final step
            </p>
            <h2 className="text-[24px] md:text-[28px] font-bold text-white leading-tight text-center max-w-xl mb-2">
              Pick your plan.
            </h2>
            <p className="text-[13px] text-gray-400 text-center max-w-md mb-6">
              Your founding-member spot is held - one click to claim it.
            </p>

            {/* Founders-club reservation card - visual, not text-only.
                Frames the moment as "you've already got a reserved spot,
                don't lose it" instead of the jargon word "provisioned". */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-xl mb-5 rounded-xl border border-primary-500/40 p-4 flex items-center gap-3"
              style={{
                background: 'linear-gradient(90deg, rgba(91,58,58,0.18), rgba(91,58,58,0.04))',
                boxShadow: '0 0 40px rgba(91,58,58,0.3)',
              }}
            >
              <div className="w-9 h-9 rounded-lg border-2 border-white bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
                <img src="/wolfromulus.png" alt="Romulus" className="w-7 h-7 object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-primary-300 mb-0.5">
                  Founding spot reserved
                </p>
                <p className="text-[12px] text-gray-300">
                  {/* Lifetime has no subscription to "stay" on, so the renewal
                      clause only shows for a monthly plan. */}
                  Locked-in launch pricing on every plan below. Yours to keep{selectedIsOneTime ? '.' : ' as long as you stay subscribed.'}
                </p>
              </div>
              <motion.span
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                className="w-2.5 h-2.5 rounded-full bg-green-400 flex-shrink-0"
                style={{ boxShadow: '0 0 16px rgba(34,197,94,0.8)' }}
              />
            </motion.div>

            <div className="w-full max-w-xl mb-5">
              <div className={isMobile ? 'grid grid-cols-2 gap-2.5' : 'grid grid-cols-1 sm:grid-cols-3 gap-3'}>
                {pickerPlans.map((planId) => {
                  const v = valueForPlan(planId);
                  const plan = PLAN_CONFIGS[planId];
                  const position = PLAN_POSITION[planId];
                  const isSelected = selected === planId;
                  // Never badge Lifetime "Matches your hours" — the hours answer
                  // maps to a subscription tier, so that would be a claim we did
                  // not compute.
                  const oneTime = isOneTimePlan(planId);
                  // On mobile the badge belongs on Lifetime, and it states the one
                  // thing that is actually true about it: the year-one return per
                  // dollar of plan cost is multiples of the subscription's. The
                  // hours badge stays on desktop, where the recommendation is the
                  // whole point of the three-card ladder.
                  const roiBadge = isMobile && oneTime && roiEdge ? `${roiEdge}× the ROI` : null;
                  const isRecommended = !isMobile && planId === defaultPlan && !oneTime;
                  const regularPrice = launchActive && planId !== 'free'
                    ? LAUNCH_REGULAR_PRICES[planId as 'starter' | 'cashflow' | 'scale' | 'agency']
                    : null;
                  return (
                    <button
                      key={planId}
                      onClick={() => {
                        haptic(8);
                        setSelected(planId);
                        logAnalyticsEvent('onboarding_plan_switched', { plan_id: planId });
                      }}
                      className={`relative text-left ${isMobile ? 'p-3' : 'p-4'} rounded-xl border transition-all ${
                        isSelected
                          ? 'border-primary-400 bg-primary-500/[0.12]'
                          : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.05]'
                      }`}
                      style={isSelected ? { boxShadow: '0 0 30px rgba(91,58,58,0.4)' } : undefined}
                    >
                      {(roiBadge || isRecommended) && (
                        <span className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-primary-500 text-[9px] font-bold tracking-[0.15em] uppercase text-white shadow-lg">
                          {roiBadge ?? 'Matches your hours'}
                        </span>
                      )}
                      <p className={`text-[13px] font-bold mb-1 ${isSelected ? 'text-white' : 'text-gray-200'}`}>{plan.name}</p>
                      <div className="flex items-baseline gap-1.5">
                        <p className="text-[20px] font-bold text-white tabular-nums">
                          ${plan.price}
                          <span className="text-[11px] text-gray-500 font-normal">{oneTime ? ' once' : '/mo'}</span>
                        </p>
                        {regularPrice && regularPrice !== plan.price && (
                          <span className="text-[11px] text-gray-500 line-through tabular-nums">${regularPrice}</span>
                        )}
                      </div>
                      <p className={`text-[10px] text-gray-500 mt-2 mb-3 ${isMobile ? 'leading-snug' : 'whitespace-nowrap'}`}>
                        {plan.quotas.leadsLimit.toLocaleString()} leads · {plan.quotas.sitesLimit} sites{oneTime ? ' total' : '/mo'}
                      </p>
                      {/* MOBILE shows year-one ROI, not take-home.
                          Take-home/mo is the wrong metric for this screen: the
                          two mobile cards differ by exactly the monthly fee, so
                          it printed $2,500 next to $2,451 and made a $79-once
                          plan look identical to a $49-a-month one. The number
                          that actually separates them is what each costs to run
                          for a year - $79 against $588 - which is what ROI
                          divides by. Desktop keeps take-home: its three cards
                          have different quotas, so their take-homes genuinely
                          diverge, and that ladder is legacy. */}
                      <div className="pt-3 border-t border-white/5">
                        {isMobile ? (
                          <>
                            <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Year-one ROI</p>
                            <p className={`text-[18px] font-bold tabular-nums ${isSelected ? 'text-primary-300' : 'text-gray-300'}`}>
                              {Math.round(v.roi).toLocaleString('en-US')}×
                            </p>
                            <p className="text-[9px] text-gray-600 mt-0.5 tabular-nums">
                              you pay {fmt(v.yearCost)}{oneTime ? ' once' : ' / yr'}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Your take-home / mo</p>
                            <p className={`text-[18px] font-bold tabular-nums ${isSelected ? 'text-primary-300' : 'text-gray-300'}`}>
                              {fmt(v.netMonthly)}
                            </p>
                          </>
                        )}
                      </div>
                      {position && (
                        <div className="mt-3 pt-2.5 border-t border-white/5">
                          <p className="text-[10px] text-gray-500 leading-snug">{position}</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="w-full max-w-xl mb-3 flex items-center justify-center gap-2">
              <motion.span
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                className="w-1.5 h-1.5 rounded-full bg-green-400"
              />
              <p className="text-[11px] text-gray-400">
                Your founder spot is held. <span className="text-white font-semibold">${selectedValue.planPrice}</span> to claim it.
              </p>
            </div>

            <div className="w-full max-w-md">
              <Button
                size="lg"
                loading={loading}
                onClick={() => onCheckout(selected)}
                className="w-full h-14 text-base font-semibold group"
              >
                <span className="flex items-center justify-center gap-2">
                  Claim my founder spot · ${selectedValue.planPrice}{selectedIsOneTime ? ' once' : '/mo'}
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
              <p className="text-[10px] text-gray-600 text-center mt-3">
                Founder pricing locked in. {selectedIsOneTime ? 'One payment, no renewal.' : 'Cancel anytime.'} 100% of every sale is yours.
              </p>
              {/* MOBILE ONLY. These links are the ONLY route to the policies from
                  a phone: HomePage's footer is `hidden md:block`, and on mobile the
                  funnel opens over the landing page, so nothing else is reachable.
                  This is the surface that takes the money, which is where Stripe's
                  acceptable-use checklist and TikTok's ad review expect terms and
                  contact to be. Do not remove without giving mobile another route.

                  Hidden on desktop so the desktop funnel stays byte-identical to
                  the legacy one - desktop reaches the same links from the landing
                  page footer, which is visible there. */}
              {isMobile && (
              <p className="text-[10px] text-gray-600 text-center mt-2 flex items-center justify-center gap-2">
                <Link to="/terms" className="underline underline-offset-2 hover:text-gray-400">Terms</Link>
                <span aria-hidden="true">·</span>
                <Link to="/privacy" className="underline underline-offset-2 hover:text-gray-400">Privacy</Link>
                <span aria-hidden="true">·</span>
                {/* mailto, not /support - that route is nested under /dashboard
                    and 404s for the logged-out guest this paywall is shown to. */}
                <a href="mailto:support@romulus.app" className="underline underline-offset-2 hover:text-gray-400">Contact</a>
              </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MOBILE SIMULATION (grafted 2026-07-24)
//
// MobileValueReveal is the phone-specific walkthrough: tap targets, a coached
// beat sequence, the niche chosen INSIDE the sim rather than by a quiz in front
// of it, and a full-page site reveal. Desktop deliberately keeps the legacy
// FunnelSimulation above - this is the single intentional divergence between
// the two platforms. Everything else (questions, plan picker) is shared.
// ═══════════════════════════════════════════════════════════════════════════

// ── Mobile value demo ────────────────────────────────────────────────────────
// Replaces the step-5 skip that commit 813eb92 introduced, and replaces the
// passive loader that first replaced it.
//
// This is a guided, tap-through tutorial of the real product loop:
//   campaign -> niche -> scrape -> pick a lead -> generate -> price -> send.
//
// Three reasons it is shaped like a game tutorial rather than a video:
//
// 1. Cold social traffic arrives having been TOLD this exact script ("go to
//    romulus.app, make a campaign, pick a niche, generate a site, send it").
//    Every step of that script we don't show is a gap between what they were
//    promised and what they see. Closing the gap is free conversion.
// 2. The endowment ("IKEA") effect only pays out on an artifact the user helped
//    build and actually finished. Watching earns nothing; tapping earns
//    ownership. It also has to COMPLETE - a half-built artifact is worth less
//    than none.
// 3. Paywalls that show real product UI beat ones that show abstract art. The
//    standard failure of a long onboarding is the user reaching the price still
//    not knowing what the app looks like.
//
// It is a simulation of the product, on mock leads, and the copy says so. We
// have not scraped anything for them yet and must not imply we have.
type TutorialBeat =
  | 'campaign'
  | 'niche'
  | 'scrape'
  | 'scraping'
  | 'leads'
  | 'building'
  | 'site'
  | 'send'
  | 'done'
  // The desktop sim's stopwatch reveal, now on mobile too. It is the beat that
  // turns "I pressed some buttons" into a number, and it is the number the
  // paywall's ROI math is built on top of.
  | 'stopwatch';

// Beats that advance on their own (the machine is working). Everything else
// waits for a tap, so the user is driving.
const AUTO_BEATS: Partial<Record<TutorialBeat, { next: TutorialBeat; ms: number }>> = {
  scraping: { next: 'leads',  ms: 2600 },
  building: { next: 'site',   ms: 2200 },
};

// Beats that are just the user pressing a button - rendered big and centred, with
// no phone chrome around them. The panel only appears once we're "inside the app".
const BARE_BEATS: TutorialBeat[] = ['campaign', 'niche', 'scrape'];

// The instruction shown above the phone. One job per beat, phrased the way the
// source video phrases it.
// Each title names the button the source video told them to press, in the same
// words. That is the whole point of the walkthrough: they were promised this
// exact sequence, so every step should feel like the thing they came to do.
const BEAT_COPY: Record<TutorialBeat, { step: number | null; title: string }> = {
  // The campaign beat is the first thing a phone visitor sees now that the mobile
  // landing page is gone, so it carries the logo and the framing line as well as the
  // instruction — see the hero block in MobileValueReveal.
  campaign: { step: 1, title: 'First, create a campaign.' },
  niche:    { step: 2, title: 'Pick who you want to sell to.' },
  scrape:   { step: 3, title: 'Now scrape the leads.' },
  scraping: { step: 3, title: 'Finding the ones with no website.' },
  leads:    { step: 4, title: 'Pick one. We build their site.' },
  building: { step: 4, title: 'Building the site.' },
  site:     { step: 5, title: 'Done. About 40 seconds.' },
  send:     { step: 5, title: 'Set your price and send it.' },
  done:     { step: null, title: '' },
  stopwatch: { step: null, title: '' },
};

const TUTORIAL_STEPS = 5;

// Pulsing ring that says "tap here". The whole tutorial rests on the user
// always knowing exactly what to press.
function TapTarget({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      className="relative w-full"
      whileTap={{ scale: 0.97 }}
      animate={{
        boxShadow: [
          '0 0 0 0 rgba(176,128,128,0)',
          '0 0 0 8px rgba(176,128,128,0.16)',
          '0 0 0 0 rgba(176,128,128,0)',
        ],
      }}
      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      style={{ borderRadius: 12 }}
    >
      {children}
    </motion.button>
  );
}

// A believable full webpage, not a gallery thumbnail.
//
// MiniSitePreview is ~90px tall by design - it's built to sit in a 3-column
// template grid. Using it for the "here is the site you just built" moment made
// the artifact look like a pancake, which actively undercuts the reveal: the
// labour illusion inverts when the payoff looks cheap, and a weak artifact scores
// worse than showing nothing at all. This renders a real page silhouette - hero,
// services, reviews, contact, footer - at a page-like aspect ratio.
function SitePagePreview({ t, business, phone }: { t: TemplateTheme; business: string; phone?: string }) {
  const isDark = t.bg.startsWith('#0') || t.bg.startsWith('#1');
  const textMain = isDark ? '#ffffff' : '#111827';
  const textSub = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(17,24,39,0.5)';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(17,24,39,0.035)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.09)' : 'rgba(17,24,39,0.07)';
  const bar = (w: string, h = 4, op = 0.45) => (
    <div className="rounded" style={{ width: w, height: h, background: textSub, opacity: op }} />
  );

  return (
    <div className={`w-full overflow-hidden ${t.heading}`} style={{ background: t.bg }}>
      {/* nav */}
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: cardBorder }}>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ background: t.primary }} />
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: textMain }}>
            {business.split(' ')[0]}
          </span>
        </div>
        <div className="flex gap-2">
          {['Home', 'Services', 'Contact'].map((l) => (
            <span key={l} className="text-[6.5px]" style={{ color: textSub }}>{l}</span>
          ))}
        </div>
      </div>

      {/* hero */}
      <div
        className="px-3 pt-4 pb-4"
        style={{ background: `linear-gradient(160deg, ${t.primary}30, transparent 70%)` }}
      >
        <div className="h-2 rounded w-4/5 mb-1.5" style={{ background: textMain, opacity: 0.9 }} />
        <div className="h-2 rounded w-3/5 mb-2.5" style={{ background: textMain, opacity: 0.9 }} />
        {bar('92%', 3)}
        <div className="h-[3px] my-1" />
        {bar('72%', 3)}
        <div className="flex gap-1.5 mt-3">
          <div className="h-[13px] w-[52px] rounded" style={{ background: t.primary }} />
          <div className="h-[13px] w-[40px] rounded border" style={{ borderColor: t.primary }} />
        </div>
      </div>

      {/* photo strip */}
      <div className="grid grid-cols-3 gap-1 px-3 pb-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-[34px] rounded"
            style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`, opacity: 0.85 - i * 0.15 }}
          />
        ))}
      </div>

      {/* services */}
      <div className="px-3 pb-3">
        <div className="h-1.5 rounded w-1/3 mb-2" style={{ background: textMain, opacity: 0.85 }} />
        <div className="space-y-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded px-2 py-1.5 flex items-center gap-2"
              style={{ background: cardBg, border: `0.5px solid ${cardBorder}` }}
            >
              <div className="w-[9px] h-[9px] rounded shrink-0" style={{ background: t.accent }} />
              <div className="flex-1">
                <div className="h-[3px] rounded w-2/5 mb-1" style={{ background: textMain, opacity: 0.75 }} />
                {bar('80%', 2.5, 0.35)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* reviews */}
      <div className="px-3 pb-3">
        <div className="rounded px-2 py-2" style={{ background: cardBg, border: `0.5px solid ${cardBorder}` }}>
          <div className="flex gap-0.5 mb-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="w-[5px] h-[5px] rounded-sm" style={{ background: t.accent }} />
            ))}
          </div>
          {bar('95%', 2.5, 0.4)}
          <div className="h-[3px]" />
          {bar('70%', 2.5, 0.4)}
        </div>
      </div>

      {/* contact / footer */}
      <div
        className="px-3 py-2.5 flex items-center justify-between border-t"
        style={{ borderColor: cardBorder, background: `${t.primary}18` }}
      >
        <div>
          <div className="h-[3px] rounded w-[46px] mb-1" style={{ background: textMain, opacity: 0.7 }} />
          <span className="text-[6.5px] tabular-nums" style={{ color: textSub }}>
            {phone || '(555) 014-2277'}
          </span>
        </div>
        <div className="h-[13px] w-[46px] rounded" style={{ background: t.primary }} />
      </div>
    </div>
  );
}

function MobileValueReveal({
  answers,
  onNiche,
  onContinue,
}: {
  answers: FunnelAnswers;
  onNiche: (id: NicheId) => void;
  onContinue: (elapsedMs: number) => void;
}) {
  const [beat, setBeat] = useState<TutorialBeat>('campaign');
  const [leadsShown, setLeadsShown] = useState(0);
  const [startedAt] = useState(() => performance.now());
  // The niche is chosen INSIDE the walkthrough now, not by a quiz in front of it.
  const [picked, setPicked] = useState<NicheId | null>(answers.niche ?? null);
  // Free-form, because the product is free-form. Deliberately NOT a set of
  // suggested tiers: "$500 - Sweet spot" is us recommending an amount, which is
  // a representation about what they can earn. An empty field they fill in
  // themselves is the product's actual behaviour and says nothing at all.
  // Prefilled, not blank (2026-07-24). Typing a number on a phone keyboard
  // mid-walkthrough is the highest-friction moment in the whole flow, and the
  // desktop sim already seeds the same $299. Still fully editable.
  const [price, setPrice] = useState('299');
  // Frozen the moment the walkthrough is finished, not read at render: the
  // stopwatch counts up to a fixed number, so it must not keep moving under it.
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const nicheLabel = picked
    ? (NICHE_OPTIONS.find((o) => o.id === picked)?.label ?? 'Plumbers')
    : 'Plumbers';
  const nicheLower = nicheLabel.toLowerCase();

  // NOTE: `hours` and `goal` no longer feed anything the user sees here.
  //
  // They used to drive an income projection on the final beat. That projection is
  // gone - a personalised earnings figure in a pre-purchase funnel is a financial
  // performance representation, and the "it's only a simulation" and "a target,
  // not a promise" defences both fail: a disclaimer cannot cure a claim it
  // contradicts. Removing the number is not a copy tweak, it is the only fix.
  //
  // Which leaves those two questions decorative, and decorative questions are
  // pure drop-off. They should either be cut or repointed at something we can
  // legally show. That is a product decision, flagged, not silently made here.
  const leads = (NICHE_MOCK_LEADS[nicheLabel] ?? NICHE_MOCK_LEADS.Plumbers).slice(0, 3);
  const business = leads[0];
  // Warm Artisan reads as a local-service site rather than a SaaS landing page,
  // which is what these buyers actually sell.
  const template = MOCK_TEMPLATES[1];

  const go = (next: TutorialBeat, pattern: number | number[] = 10) => {
    haptic(pattern);
    setBeat(next);
  };

  // Auto-advancing beats (scraping, building).
  useEffect(() => {
    const auto = AUTO_BEATS[beat];
    if (!auto) return;
    const t = setTimeout(() => {
      haptic([10, 40, 16]);
      setBeat(auto.next);
    }, auto.ms);
    return () => clearTimeout(t);
  }, [beat]);

  // Leads land one at a time so the scrape reads as work being done rather than
  // a list that was always there.
  useEffect(() => {
    if (beat !== 'leads') { if (beat === 'scraping') setLeadsShown(0); return; }
    if (leadsShown >= leads.length) return;
    const t = setTimeout(() => { haptic(6); setLeadsShown((n) => n + 1); }, 260);
    return () => clearTimeout(t);
  }, [beat, leadsShown, leads.length]);

  const copy = BEAT_COPY[beat];
  const isBare = BARE_BEATS.includes(beat);
  const isFirstBeat = beat === 'campaign';

  return (
    <div className="flex-1 flex flex-col w-full max-w-md mx-auto">
      <AnimatePresence mode="wait">
        {beat === 'stopwatch' ? (
          /* The stopwatch, same component desktop uses. It has to come AFTER the
             "you just built a website" card rather than replacing it: the card is
             what they did, the clock is how long it took, and the clock only
             means anything once the thing it timed has been named. */
          <RevealStage
            key="stopwatch"
            ms={elapsedMs ?? 0}
            count={leads.length}
            ctaLabel="Show me the numbers"
            onNext={() => { haptic(12); onContinue(elapsedMs ?? 0); }}
          />
        ) : beat !== 'done' ? (
          <motion.div
            /* Keyed on whether the hero is present, NOT a constant.
               The hero is ~250px and renders on the first beat only, so leaving
               that beat reflows the entire column upward - and with the column
               vertically centred, the instruction jumped about 200px in a single
               frame. Changing the key here makes AnimatePresence cross-fade the
               whole screen across that one swap, so the reflow happens at opacity
               zero. Every later beat shares the key and does not re-fade. */
            key={isFirstBeat ? 'tutorial-intro' : 'tutorial'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="flex-1 flex flex-col pt-1"
          >
            {/* The campaign beat is the door. There is no mobile landing page behind
                this any more and no way to dismiss the funnel, so this screen has to
                do the job the hero used to do: say whose product this is (the mark),
                say what we are about to do (the framing line), and give them the one
                button to press. Every later beat drops back to the plain instruction
                header — the identity only needs establishing once. */}
            {isFirstBeat && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col items-center text-center pt-2 mb-5"
              >
                <div
                  className="w-16 h-16 rounded-2xl border-2 border-white bg-black flex items-center justify-center overflow-hidden mb-4"
                  style={{ boxShadow: '0 0 44px rgba(91,58,58,0.5)' }}
                >
                  <img src="/wolfromulus.png" alt="Romulus" className="w-12 h-12 object-contain" />
                </div>
                <span className="text-[13px] font-bold tracking-[0.28em] text-white font-roman mb-4">
                  ROMVLVS
                </span>
                <h1 className="text-[26px] font-bold text-white leading-tight px-2">
                  Let's practice making sales.
                </h1>
              </motion.div>
            )}

            {/* Instruction. One job at a time, always above the phone. */}
            <div className={`text-center mb-4 ${isFirstBeat ? '' : 'min-h-[62px]'}`}>
              {copy.step && (
                <p className="text-primary-400 text-[10px] font-semibold tracking-[0.22em] uppercase mb-1.5">
                  Step {copy.step} of {TUTORIAL_STEPS}
                </p>
              )}
              <AnimatePresence mode="wait">
                <motion.h2
                  key={copy.title}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25 }}
                  className="text-[21px] font-bold text-white leading-tight px-3"
                >
                  {copy.title}
                </motion.h2>
              </AnimatePresence>
            </div>

            {/* The phone chrome only wraps the beats that happen INSIDE the app.
                Pressing "+ New campaign" and picking a niche are big, bare,
                centred actions - shrinking them into a 300px frame made the two
                simplest taps in the funnel the fiddliest. */}
            {/* minHeight is the SAME on bare and framed beats, and that is what
                stops the walkthrough snapping between steps. AnimatePresence
                mode="wait" unmounts the outgoing beat before mounting the next,
                so for one frame this box has no children - and without a floor it
                collapsed to zero, yanking everything above it up and then
                dropping it back. Matching the framed panel's height also means
                bare -> framed (scrape -> scraping) no longer resizes. */}
            <div
              className={isBare ? 'w-full flex flex-col justify-center' : 'rounded-[22px] overflow-hidden flex flex-col'}
              style={
                isBare
                  ? { minHeight: 372 }
                  : {
                      background: '#08080a',
                      border: '1px solid rgba(255,255,255,0.12)',
                      boxShadow: '0 24px 70px rgba(0,0,0,0.65), 0 0 44px rgba(176,128,128,0.13)',
                      minHeight: 372,
                    }
              }
            >
              {!isBare && (
                <div
                  className="flex items-center justify-between px-3.5 py-2.5"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <span className="text-[11px] font-bold tracking-wider text-white font-roman">
                    ROMVLVS
                  </span>
                  <span className="text-[9px] text-gray-500 uppercase tracking-wider">
                    {beat === 'send' || beat === 'site' ? 'Sites' : 'Leads'}
                  </span>
                </div>
              )}

              <div className={isBare ? '' : 'flex-1 p-3.5 flex flex-col'}>
                <AnimatePresence mode="wait">
                  {/* 1 ─ make a campaign. One big button, nothing else. */}
                  {beat === 'campaign' && (
                    <motion.div
                      key="b-campaign"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="w-full flex flex-col items-center pt-2"
                    >
                      <div className="w-full max-w-[300px]">
                        {/* The app's one solid primary button, at its own colour
                            token and corner radius rather than a hand-typed hex,
                            so the walkthrough and the product cannot drift. */}
                        <TapTarget onClick={() => go('niche')}>
                          <div className="w-full rounded-lg py-5 text-[17px] font-semibold text-white flex items-center justify-center gap-2 bg-primary-500 shadow-lg shadow-primary-500/25">
                            <Plus className="w-4 h-4" />
                            New Campaign
                          </div>
                        </TapTarget>
                      </div>
                      <p className="text-[12px] text-gray-600 mt-5 text-center">
                        Takes about 40 seconds, start to finish.
                      </p>
                    </motion.div>
                  )}

                  {/* 2 ─ the wheel. Tap a slice.
                      Note for the record: tapered SVG wedges are a real mis-tap
                      risk (they narrow to slivers toward the hub), and plain tap
                      cards complete better in every controlled comparison. The
                      wheel stays because it is the product's signature moment and
                      the founder wants it - so it is rendered full-width at its
                      natural size rather than shrunk inside the phone frame. */}
                  {beat === 'niche' && (
                    <motion.div
                      key="b-niche"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="w-full"
                    >
                      <FunnelNicheStep
                        bare
                        onPick={(id) => {
                          setPicked(id);
                          onNiche(id);
                          go('scrape', [10, 30, 12]);
                        }}
                      />
                    </motion.div>
                  )}

                  {/* 3 ─ "hit scrape leads". The video names this button, so the
                      walkthrough has to contain it. It used to auto-run. */}
                  {beat === 'scrape' && (
                    <motion.div
                      key="b-scrape"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="w-full flex flex-col items-center pt-2"
                    >
                      <p className="text-[13px] text-gray-500 mb-6 text-center">
                        Romulus will pull every {nicheLower.replace(/s$/, '')} near you with no website.
                      </p>
                      {/* Outline, not filled. In the real app this is
                          <Button variant="outline"> on the campaign card -
                          transparent over black with a hairline border and white
                          label - while "+ New campaign" is the one solid
                          primary button on the page. The walkthrough had both
                          rendered as solid mauve, so the first thing a new user
                          did inside the product was hunt for a button that did
                          not look like the one they were shown. */}
                      <div className="w-full max-w-[300px]">
                        <TapTarget onClick={() => go('scraping', [10, 30, 12])}>
                          <div className="w-full rounded-lg py-4 text-[16px] font-semibold text-white flex items-center justify-center gap-2 border border-white/10 bg-transparent">
                            <Search className="w-4 h-4" />
                            Scrape Leads
                          </div>
                        </TapTarget>
                      </div>
                    </motion.div>
                  )}

                  {/* 3a ─ the scrape (auto) */}
                  {beat === 'scraping' && (
                    <motion.div
                      key="b-scraping"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="h-full min-h-[320px] flex flex-col justify-center items-center text-center px-2"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                        className="w-9 h-9 rounded-full border-2 border-primary-400 border-t-transparent mb-4"
                      />
                      <p className="text-[13px] text-white font-medium mb-1">
                        Scanning {nicheLower} near you
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Filtering out anyone who already has a website
                      </p>
                    </motion.div>
                  )}

                  {/* 3b ─ leads land, pick one */}
                  {beat === 'leads' && (
                    <motion.div
                      key="b-leads"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="h-full flex flex-col"
                    >
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2.5">
                        {leadsShown} found · no website
                      </p>
                      <div className="space-y-2">
                        {leads.slice(0, leadsShown).map((l, i) => {
                          const row = (
                            <div
                              className="rounded-xl px-3 py-2.5 flex items-center justify-between gap-2 w-full text-left"
                              style={{
                                background: i === 0 ? 'rgba(176,128,128,0.12)' : 'rgba(255,255,255,0.03)',
                                border: `1px solid ${i === 0 ? 'rgba(176,128,128,0.8)' : 'rgba(255,255,255,0.07)'}`,
                              }}
                            >
                              <div className="min-w-0">
                                <p className="text-[12.5px] text-white font-semibold truncate">{l.name}</p>
                                <p className="text-[10.5px] text-gray-500 tabular-nums">
                                  {l.rating}★ · {l.reviewCount} reviews
                                </p>
                              </div>
                              {i === 0 && (
                                <span
                                  className="text-[10px] font-bold text-white px-2 py-1 rounded-lg shrink-0"
                                  style={{ background: '#B08080' }}
                                >
                                  Build site
                                </span>
                              )}
                            </div>
                          );
                          return (
                            <motion.div
                              key={l.name}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.28 }}
                            >
                              {i === 0 && leadsShown === leads.length ? (
                                <TapTarget onClick={() => go('building', [10, 30, 12])}>{row}</TapTarget>
                              ) : (
                                row
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* 4a ─ generating (auto) */}
                  {beat === 'building' && (
                    <motion.div
                      key="b-building"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="h-full min-h-[320px] flex flex-col justify-center items-center text-center px-2"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                        className="w-9 h-9 rounded-full border-2 border-primary-400 border-t-transparent mb-4"
                      />
                      <p className="text-[13px] text-white font-medium mb-1">
                        Writing {business.name} a website
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Their reviews, hours and photos, pulled in automatically
                      </p>
                    </motion.div>
                  )}

                  {/* 4b ─ the finished artifact */}
                  {beat === 'site' && (
                    <motion.div
                      key="b-site"
                      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full flex flex-col"
                    >
                      {/* Clipped to a fixed window. The page is deliberately
                          longer than the frame - you are looking at the top of a
                          real site, not a thumbnail of a whole one - and clipping
                          keeps the CTA below it on screen. */}
                      <div
                        className="rounded-xl overflow-hidden border border-white/10 mb-3 relative"
                        style={{ height: 250 }}
                      >
                        <SitePagePreview t={template} business={business.name} />
                        <div
                          className="absolute inset-x-0 bottom-0 h-10 pointer-events-none"
                          style={{ background: 'linear-gradient(to top, rgba(8,8,10,0.9), transparent)' }}
                        />
                      </div>
                      <TapTarget onClick={() => go('send')}>
                        <div
                          className="w-full rounded-xl py-2.5 text-[13.5px] font-bold text-white"
                          style={{ background: '#B08080' }}
                        >
                          Set a price
                        </div>
                      </TapTarget>
                    </motion.div>
                  )}

                  {/* 5 ─ price it and send. Seeded at $299 to match the desktop
                      sim's default, so the user taps through instead of stopping
                      to type on a phone keyboard. The field stays editable - they
                      can name any number, exactly as the product allows. */}
                  {beat === 'send' && (
                    <motion.div
                      key="b-send"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="h-full flex flex-col justify-center"
                    >
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
                        Your price
                      </p>
                      <div
                        className="rounded-xl px-3.5 py-3 mb-3 flex items-center gap-1.5"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
                      >
                        <span className="text-[24px] font-bold text-gray-500">$</span>
                        <input
                          value={price}
                          onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                          inputMode="numeric"
                          placeholder="You decide"
                          className="flex-1 bg-transparent outline-none text-[22px] font-bold text-white placeholder:text-gray-600 placeholder:text-[15px] placeholder:font-normal tabular-nums"
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 mb-3.5 truncate">
                        To {business.name}
                      </p>
                      <TapTarget onClick={() => go('done', [12, 50, 20])}>
                        <div
                          className="w-full rounded-xl py-3 text-[14px] font-bold text-white flex items-center justify-center gap-2"
                          style={{ background: '#B08080' }}
                        >
                          <Send className="w-4 h-4" />
                          Send to Business
                        </div>
                      </TapTarget>
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>
            </div>

            <p className="text-center text-[10.5px] text-gray-600 mt-3 px-4">
              A walkthrough on sample businesses. You run it on real ones in your area.
            </p>
          </motion.div>
        ) : (
          /* ── Result. The loop is closed: they built and sent something. ── */
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 16, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex-1 flex flex-col pt-2"
          >
            <div className="text-center mb-5">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{ background: 'rgba(176,128,128,0.18)', border: '1px solid rgba(176,128,128,0.6)' }}
              >
                <Check className="w-6 h-6 text-primary-300" strokeWidth={3} />
              </motion.div>
              <h2 className="text-[26px] font-bold text-white leading-tight mb-1.5">
                You just built a website.
              </h2>
              <p className="text-[14px] text-gray-400 px-3">
                A real {nicheLower.replace(/s$/, '')}, a finished site, sent. You wrote no code.
              </p>
            </div>

            {/* Capability recap, NOT an income projection.
                An earnings figure in a pre-purchase funnel is a financial
                performance representation: it is never puffery, a "potential" or
                "simulation" label does not cure it, and a disclaimer cannot
                contradict a claim it is attached to. So there is no dollar figure
                here and no projected monthly. What is celebrated instead is the
                thing we can actually prove on screen - the user built a real site,
                from nothing, in seconds. That is a product-capability claim, it is
                substantiable, and it is the stronger persuasion anyway: the peak
                of this funnel is the artifact appearing, not a number counting up. */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }}
              className="grid grid-cols-3 gap-2"
            >
              {[
                { k: 'Found', v: `${leads.length}`, s: 'with no site' },
                { k: 'Built', v: '1', s: 'full website' },
                { k: 'Code written', v: '0', s: 'lines' },
              ].map((c) => (
                <div
                  key={c.k}
                  className="rounded-xl px-2 py-2.5 text-center bg-white/[0.04] border border-white/[0.07]"
                >
                  <p className="text-white font-bold text-[19px] tabular-nums leading-none">{c.v}</p>
                  <p className="text-[10px] text-gray-500 mt-1 leading-tight">{c.k}</p>
                  <p className="text-[9px] text-gray-600 leading-tight">{c.s}</p>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.45 }}
              className="mt-6"
            >
              <Button
                size="lg"
                onClick={() => {
                  haptic(12);
                  setElapsedMs(performance.now() - startedAt);
                  setBeat('stopwatch');
                }}
                className="w-full h-14 text-base font-bold group rounded-full"
              >
                <span className="flex items-center justify-center gap-2">
                  Continue
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
